import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth-context';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  Users, 
  Video, 
  BookOpen, 
  Brain,
  Tag, 
  TrendingUp, 
  Plus, 
  X,
  CheckCircle,
  FileText,
  Building2,
  Briefcase,
  Phone,
  Mail,
  Shield,
  Upload,
  AlertTriangle,
  BookOpenCheck,
  Calendar,
  Link,
  ChevronDown,
  Settings,
  GraduationCap,
  Wrench,
  ChevronLeft,
  ChevronRight,
  Activity
} from 'lucide-react';
import HomeworkManagement from './HomeworkManagement';
import EventsManagement from './EventsManagement';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [activeVideoSubTab, setActiveVideoSubTab] = useState('all'); // 'founder', 'advisor', 'all'
  const [activeResourceSubTab, setActiveResourceSubTab] = useState('all'); // 'founder', 'advisor', 'all'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Add error boundary for debugging
  const handleError = (error, errorInfo) => {
    console.error('AdminDashboard Error:', error, errorInfo);
    setError(`Component Error: ${error.message}`);
  };
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_users: 0,
    per_page: 50
  });
  const [videos, setVideos] = useState([]);
  const [resources, setResources] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [homework, setHomework] = useState([]);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userAssessments, setUserAssessments] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [superAdminStatus, setSuperAdminStatus] = useState(null);
  const [advisorInviteStatus, setAdvisorInviteStatus] = useState([]);


  


  // Check if user is admin or staff
  useEffect(() => {
    // Debug: User authentication check
    
    if (user && user.role !== 'admin' && user.role !== 'staff') {
      // User is not admin or staff, redirecting to dashboard
      navigate('/dashboard');
      return;
    }
    if (user) {
      // Loading admin dashboard data (admin only)
      if (user.role === 'admin') {
        loadDashboardData();
        loadSuperAdminStatus();
        loadCategories(); // Load categories when dashboard initializes
        loadPrompts(); // Load prompts when dashboard initializes
      }
      // Staff: load assessments and set as default tab
      if (user.role === 'staff') {
        loadAssessments();
        setActiveTab('assessments');
      }
    }
  }, [user, navigate]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && !event.target.closest('.relative')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const loadDashboardData = async () => {
    try {
      // Loading dashboard data...
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/dashboard`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Dashboard data loaded successfully
        setDashboardData(data.data);
      } else {
        console.error('Failed to load dashboard data:', response.status, response.statusText);
        setError('Failed to load dashboard data');
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('Error loading dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadSuperAdminStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/super-admin/status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuperAdminStatus(data.data);
      }
    } catch (error) {
      console.error('Error loading super admin status:', error);
    }
  };

  const loadUsers = async (page = 1, limit = 50, filters = {}) => {
    try {
      // Loading users...
      const token = localStorage.getItem('authToken');
      
      // Build query string with pagination and filters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      // Add filters if provided
      if (filters.role && filters.role !== '') {
        params.append('role', filters.role);
      }
      if (filters.is_active !== undefined && filters.is_active !== '') {
        params.append('is_active', filters.is_active);
      }
      if (filters.search && filters.search.trim() !== '') {
        params.append('search', filters.search);
      }
      
      const url = `${import.meta.env.VITE_API_URL}/api/admin/users?${params.toString()}`;
      console.log('Loading users from:', url);
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Users loaded successfully
        setUsers(data.data.users);
        // Store pagination info
        if (data.data.pagination) {
          console.log('Pagination data received:', data.data.pagination);
          setUsersPagination(data.data.pagination);
        } else {
          console.warn('No pagination data in response:', data);
        }
      } else {
        console.error('Failed to load users:', response.status, response.statusText);
        setError('Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Error loading users');
    }
  };

  // Refetch users with current pagination (used when operations like edit/delete complete)
  const refreshUsers = () => {
    // Reload with current pagination state if available, otherwise default
    const currentPage = usersPagination?.current_page || 1;
    loadUsers(currentPage, 50, {}); // No filters - just refresh current page
  };

  // Advisor invite functions
  const loadAdvisorInviteStatus = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/advisor-invite-status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAdvisorInviteStatus(data.advisors || []);
      }
    } catch (error) {
      console.error('Error loading advisor invite status:', error);
    }
  };

  // Generic user invite status functions
  const [userInviteStatus, setUserInviteStatus] = useState([]);

  const loadUserInviteStatus = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/user-invite-status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserInviteStatus(data.users || []);
      }
    } catch (error) {
      console.error('Error loading user invite status:', error);
    }
  };

  const handleSendAdvisorInvite = async (email, name, company, role = 'advisor') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/send-advisor-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          email,
          name,
          company,
          role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send advisor invite');
      }

      const result = await response.json();
      alert(`${role === 'staff' ? 'Staff' : role === 'investor' ? 'Investor' : 'Advisor'} invite sent successfully to ${email}!`);
      
      // Refresh both users and advisor status
      refreshUsers();
      loadAdvisorInviteStatus();
    } catch (error) {
      console.error('Error sending advisor invite:', error);
      alert('Failed to send advisor invite: ' + error.message);
    }
  };

  const handleResendAdvisorInvite = async (email, name, company, role = 'advisor') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/send-advisor-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          email,
          name,
          company,
          role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to resend advisor invite');
      }

      const result = await response.json();
      alert(`${role === 'staff' ? 'Staff' : role === 'investor' ? 'Investor' : 'Advisor'} invite resent successfully to ${email}!`);
      
      // Refresh both users and advisor status
      refreshUsers();
      loadAdvisorInviteStatus();
    } catch (error) {
      console.error('Error resending advisor invite:', error);
      alert('Failed to resend advisor invite: ' + error.message);
    }
  };

  // Generic user invite function for all user types
  const handleSendUserInvite = async (email, name, company, userType = 'user') => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/send-user-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          email,
          name,
          company,
          userType
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send user invite');
      }

      const result = await response.json();
      alert(`Password setup email sent successfully to ${email}!`);
      
      // Refresh users list and invite status
      refreshUsers();
      loadUserInviteStatus();
    } catch (error) {
      console.error('Error sending user invite:', error);
      alert('Failed to send user invite: ' + error.message);
    }
  };

  const loadVideos = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/videos`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVideos(data.data.videos);
      }
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  };

  const loadResources = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/resources`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResources(data.data.resources);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
    }
  };

  const loadPrompts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts?t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPrompts(data.data);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/categories`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Categories loaded successfully
        setCategories(data.data.categories);
      } else {
        console.error('Failed to load categories:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadHomework = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/homework/admin/all`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Homework loaded successfully
        setHomework(data.data.homework);
      } else {
        console.error('Failed to load homework:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading homework:', error);
    }
  };

  const loadEvents = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/events`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Events loaded successfully
        setEvents(data.data.events);
      } else {
        console.error('Failed to load events:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const loadAssessments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/assessment/admin/all`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAssessments(data.assessments || []);
      } else {
        console.error('Failed to load assessments:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading assessments:', error);
    }
  };

  const viewUserAssessments = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/assessment/admin/user/${userId}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserAssessments(data.assessments || []);
        setSelectedUserId(userId);
      } else {
        console.error('Failed to load user assessments:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading user assessments:', error);
    }
  };

  const handleTabChange = (tab) => {
    // Staff can only access assessments tab
    if (user?.role === 'staff' && tab !== 'assessments') {
      setActiveTab('assessments');
      loadAssessments();
      return;
    }
    
    setActiveTab(tab);
    setShowCreateForm(false);
    setEditingItem(null);
    setOpenDropdown(null); // Close any open dropdowns
    
    // Load data for the selected tab
    switch (tab) {
      case 'users':
        loadUsers(1, 50, {}); // Load page 1 with no filters when switching tabs
        break;
      case 'videos':
        loadVideos();
        break;
      case 'resources':
        loadResources();
        break;
      case 'prompts':
        loadPrompts();
        break;
      case 'homework':
        loadHomework();
        break;
      case 'events':
        loadEvents();
        break;
      case 'categories':
        loadCategories();
        break;
      case 'assessments':
        loadAssessments();
        break;
      default:
        break;
    }
  };

  const handleDropdownToggle = (dropdownId) => {
    setOpenDropdown(openDropdown === dropdownId ? null : dropdownId);
  };

  const handleDropdownItemClick = (tabId, isLink = false, link = null) => {
    if (isLink && link) {
      navigate(link);
    } else {
      handleTabChange(tabId);
    }
    setOpenDropdown(null);
  };

  const handleEditUser = (user) => {
    setEditingItem(user);
  };

  const handleDeactivateUser = async (userId, reactivate = false) => {
    const action = reactivate ? 'reactivate' : 'deactivate';
    const isActive = reactivate;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ is_active: isActive })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${action} user`);
      }

      // Refresh the users list
      refreshUsers();
      
      // Show success message
      alert(`User ${action}d successfully!`);
    } catch (error) {
      console.error(`Error ${action}ing user:`, error);
      alert(`Failed to ${action} user: ` + error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete user');
      }

      // Refresh the users list
      refreshUsers();
      
      // Show success message
      alert('User deleted permanently!');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user: ' + error.message);
    }
  };

  const handleResetPassword = async (userId, userEmail) => {
    const newPassword = prompt(`Enter new password for ${userEmail} (minimum 8 characters):`);
    
    if (!newPassword) return;
    
    if (newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }

    if (window.confirm(`Are you sure you want to reset the password for ${userEmail}?`)) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${userId}/reset-password`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({ 
            new_password: newPassword,
            send_email: false // Will implement email later
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to reset password');
        }

        const result = await response.json();
        
        // Show the new password to admin
        alert(`Password reset successfully!\n\nNew password: ${result.data.new_password}\n\nPlease share this with the user securely.`);
        
        // Refresh the users list
        refreshUsers();
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Failed to reset password: ' + error.message);
      }
    }
  };

  // AdminDashboard render state check

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <AlertTriangle className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600 font-medium">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center py-6 space-y-4 lg:space-y-0">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Manage your Impact Accelerator Circle portal</p>
              {superAdminStatus?.is_super_admin && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <Shield className="w-4 h-4 mr-1" />
                    Super Admin Access
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    You can create new admin accounts
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  <Shield className="w-4 h-4 mr-1" />
                  {user?.role === 'staff' ? 'Staff Access' : 'Admin Access'}
                </span>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-gray-600 text-white px-4 py-2 rounded-xl hover:bg-gray-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Back to Portal
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-1 lg:space-x-2 pb-2 lg:pb-0">
            {/* Overview - Admin only */}
            {user?.role === 'admin' && (
              <button
                onClick={() => handleTabChange('overview')}
                className={`py-3 px-2 lg:py-4 lg:px-4 border-b-2 font-medium text-xs lg:text-sm flex items-center whitespace-nowrap rounded-lg lg:rounded-none transition-all duration-200 ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600 bg-blue-50 lg:bg-transparent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 lg:hover:bg-transparent'
                }`}
              >
                <BarChart3 className="w-4 h-4 lg:w-5 lg:h-5 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Overview</span>
                <span className="sm:hidden">Overview</span>
              </button>
            )}

            {/* User Management Dropdown - Admin only */}
            {user?.role === 'admin' && (
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('users')}
                className={`py-3 px-2 lg:py-4 lg:px-4 border-b-2 font-medium text-xs lg:text-sm flex items-center whitespace-nowrap rounded-lg lg:rounded-none transition-all duration-200 ${
                  activeTab === 'users' || activeTab === 'analytics'
                    ? 'border-blue-500 text-blue-600 bg-blue-50 lg:bg-transparent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 lg:hover:bg-transparent'
                }`}
              >
                <Users className="w-4 h-4 lg:w-5 lg:h-5 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">User Management</span>
                <span className="sm:hidden">Users</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </button>
              {openDropdown === 'users' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handleDropdownItemClick('users')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Users
                    </button>
                    <button
                      onClick={() => handleDropdownItemClick('analytics')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Analytics
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Content Management Dropdown - Admin only */}
            {user?.role === 'admin' && (
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('content')}
                className={`py-3 px-2 lg:py-4 lg:px-4 border-b-2 font-medium text-xs lg:text-sm flex items-center whitespace-nowrap rounded-lg lg:rounded-none transition-all duration-200 ${
                  activeTab === 'resources' || activeTab === 'videos' || activeTab === 'categories' || activeTab === 'prompts'
                    ? 'border-blue-500 text-blue-600 bg-blue-50 lg:bg-transparent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 lg:hover:bg-transparent'
                }`}
              >
                <BookOpen className="w-4 h-4 lg:w-5 lg:h-5 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Content Management</span>
                <span className="sm:hidden">Content</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </button>
              {openDropdown === 'content' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handleDropdownItemClick('resources')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Resources
                    </button>
                    <button
                      onClick={() => handleDropdownItemClick('videos')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Videos
                    </button>
                    <button
                      onClick={() => handleDropdownItemClick('categories')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Tag className="w-4 h-4 mr-2" />
                      Categories
                    </button>
                    <button
                      onClick={() => handleDropdownItemClick('prompts')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Brain className="w-4 h-4 mr-2" />
                      AI Prompts
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Learning Management Dropdown - Admin only */}
            {user?.role === 'admin' && (
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('learning')}
                className={`py-3 px-2 lg:py-4 lg:px-4 border-b-2 font-medium text-xs lg:text-sm flex items-center whitespace-nowrap rounded-lg lg:rounded-none transition-all duration-200 ${
                  activeTab === 'homework' || activeTab === 'events'
                    ? 'border-blue-500 text-blue-600 bg-blue-50 lg:bg-transparent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 lg:hover:bg-transparent'
                }`}
              >
                <GraduationCap className="w-4 h-4 lg:w-5 lg:h-5 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">Learning Management</span>
                <span className="sm:hidden">Learning</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </button>
              {openDropdown === 'learning' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handleDropdownItemClick('homework')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <BookOpenCheck className="w-4 h-4 mr-2" />
                      Homework
                    </button>
                    <button
                      onClick={() => handleDropdownItemClick('events')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Events
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Assessments Tab - Admin and Staff */}
            <button
              onClick={() => handleTabChange('assessments')}
              className={`py-3 px-2 lg:py-4 lg:px-4 border-b-2 font-medium text-xs lg:text-sm flex items-center whitespace-nowrap rounded-lg lg:rounded-none transition-all duration-200 ${
                activeTab === 'assessments'
                  ? 'border-blue-500 text-blue-600 bg-blue-50 lg:bg-transparent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 lg:hover:bg-transparent'
              }`}
            >
              <Activity className="w-4 h-4 lg:w-5 lg:h-5 mr-1 lg:mr-2" />
              <span className="hidden sm:inline">Assessments</span>
              <span className="sm:hidden">Assess</span>
            </button>

            {/* System Tools Dropdown - Admin only */}
            {user?.role === 'admin' && (
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('tools')}
                className={`py-3 px-2 lg:py-4 lg:px-4 border-b-2 font-medium text-xs lg:text-sm flex items-center whitespace-nowrap rounded-lg lg:rounded-none transition-all duration-200 ${
                  activeTab === 'stripe-import' || activeTab === 'guides'
                    ? 'border-blue-500 text-blue-600 bg-blue-50 lg:bg-transparent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 lg:hover:bg-transparent'
                }`}
              >
                <Wrench className="w-4 h-4 lg:w-5 lg:h-5 mr-1 lg:mr-2" />
                <span className="hidden sm:inline">System Tools</span>
                <span className="sm:hidden">Tools</span>
                <ChevronDown className="w-3 h-3 ml-1" />
              </button>
              {openDropdown === 'tools' && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handleDropdownItemClick('stripe-import')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Stripe Import
                    </button>
                    <button
                      onClick={() => handleDropdownItemClick('guides', true, '/admin/guides')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      Admin Guides
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && user?.role === 'admin' && (
          <DashboardOverview data={dashboardData} />
        )}
        
        {activeTab === 'users' && user?.role === 'admin' && (
          <UsersManagement 
            users={users}
            pagination={usersPagination}
            onLoadUsers={loadUsers}
            onRefresh={refreshUsers}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            superAdminStatus={superAdminStatus}
            onEditUser={handleEditUser}
            onDeactivateUser={handleDeactivateUser}
            onResetPassword={handleResetPassword}
            onDeleteUser={handleDeleteUser}
            currentUser={user}
            advisorInviteStatus={advisorInviteStatus}
            onLoadAdvisorInviteStatus={loadAdvisorInviteStatus}
            onSendAdvisorInvite={handleSendAdvisorInvite}
            onResendAdvisorInvite={handleResendAdvisorInvite}
            onSendUserInvite={handleSendUserInvite}
            onLoadUserInviteStatus={loadUserInviteStatus}
            userInviteStatus={userInviteStatus}
          />
        )}
        
        {activeTab === 'videos' && user?.role === 'admin' && (
          <VideosManagement 
            videos={videos} 
            categories={categories}
            onRefresh={loadVideos}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            activeSubTab={activeVideoSubTab}
            setActiveSubTab={setActiveVideoSubTab}
          />
        )}
        
        {activeTab === 'resources' && user?.role === 'admin' && (
          <ResourcesManagement 
            resources={resources} 
            categories={categories}
            onRefresh={loadResources}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
            activeSubTab={activeResourceSubTab}
            setActiveSubTab={setActiveResourceSubTab}
          />
        )}
        
        {activeTab === 'prompts' && user?.role === 'admin' && (
          <PromptsManagement 
            prompts={prompts} 
            categories={categories}
            onRefresh={loadPrompts}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
          />
        )}
        
        {activeTab === 'homework' && user?.role === 'admin' && (
          <HomeworkManagement 
            homework={homework} 
            categories={categories}
            onRefresh={loadHomework}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
          />
        )}
        
        {activeTab === 'events' && user?.role === 'admin' && (
          <EventsManagement 
            events={events} 
            onRefresh={loadEvents}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
          />
        )}
        
        {activeTab === 'categories' && user?.role === 'admin' && (
          <CategoriesManagement 
            categories={categories} 
            onRefresh={loadCategories}
            showCreateForm={showCreateForm}
            setShowCreateForm={setShowCreateForm}
            editingItem={editingItem}
            setEditingItem={setEditingItem}
          />
        )}
        
        {activeTab === 'assessments' && (
          <div className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Member Assessments</h2>
                <p className="text-gray-600">View all pitch readiness assessments</p>
              </div>
              <div className="text-sm text-gray-500">
                Total Assessments: {assessments.length}
              </div>
            </div>

            {/* Assessment Import Section */}
            <AssessmentImportView onImportComplete={loadAssessments} />
            
            <div className="mt-8">

            {/* Assessments Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Member
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verdict
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Most Recent Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Assessments
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assessments.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                        No assessments found
                      </td>
                    </tr>
                  ) : (
                    assessments.map((assessment) => {
                      const getScoreColor = (score) => {
                        if (score >= 8.0) return 'text-green-600';
                        if (score >= 6.0) return 'text-blue-600';
                        if (score >= 4.0) return 'text-yellow-600';
                        return 'text-red-600';
                      };
                      
                      return (
                        <tr key={assessment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {assessment.first_name} {assessment.last_name}
                              </div>
                              <div className="text-sm text-gray-500">{assessment.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-2xl font-bold ${getScoreColor(parseFloat(assessment.overall_score))}`}>
                              {parseFloat(assessment.overall_score).toFixed(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`text-sm font-semibold ${getScoreColor(parseFloat(assessment.overall_score))}`}>
                              {assessment.readiness_verdict}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(assessment.completed_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {assessment.total_assessments || 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => viewUserAssessments(assessment.user_id)}
                              className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors"
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            </div>

            {/* User Assessment Details Modal */}
            {selectedUserId && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold">Assessment Details</h3>
                      <p className="text-gray-600 text-sm mt-1">
                        {userAssessments.length > 0 && `${userAssessments[0].first_name} ${userAssessments[0].last_name} - ${userAssessments[0].email}`}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedUserId(null);
                        setUserAssessments([]);
                      }}
                      className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-6">
                    {userAssessments.map((assessment, idx) => {
                      const getScoreColor = (score) => {
                        if (score >= 8.0) return 'text-green-600';
                        if (score >= 6.0) return 'text-blue-600';
                        if (score >= 4.0) return 'text-yellow-600';
                        return 'text-red-600';
                      };

                      const getProgressBarColor = (score) => {
                        if (score >= 8.0) return 'bg-green-500';
                        if (score >= 6.0) return 'bg-blue-500';
                        if (score >= 4.0) return 'bg-yellow-500';
                        return 'bg-red-500';
                      };

                      const improvement = idx < userAssessments.length - 1 
                        ? parseFloat(assessment.overall_score) - parseFloat(userAssessments[idx + 1].overall_score) 
                        : null;
                      
                      return (
                        <div key={assessment.id} className="border-2 rounded-lg p-6 bg-gray-50">
                          {/* Assessment Header */}
                          <div className="flex items-center justify-between mb-6 pb-4 border-b">
                            <div>
                              <div className="text-xl font-bold mb-1">
                                Assessment #{assessment.assessment_number}
                              </div>
                              <div className="text-sm text-gray-600">
                                {new Date(assessment.completed_at).toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className={`text-4xl font-bold ${getScoreColor(parseFloat(assessment.overall_score))}`}>
                                {parseFloat(assessment.overall_score).toFixed(1)}
                              </div>
                              <div className={`text-lg font-semibold ${getScoreColor(parseFloat(assessment.overall_score))}`}>
                                {assessment.readiness_verdict}
                              </div>
                              {improvement !== null && (
                                <div className={`text-sm mt-1 ${improvement > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {improvement > 0 ? '↑' : '↓'} {Math.abs(improvement).toFixed(1)} from previous
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Verdict Description */}
                          {assessment.verdict_description && (
                            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-gray-700">{assessment.verdict_description}</p>
                            </div>
                          )}

                          {/* Section Scores */}
                          <div className="mb-6">
                            <h4 className="text-lg font-semibold mb-4">Section Breakdown</h4>
                            <div className="space-y-3">
                              {assessment.section_scores?.map((item, i) => (
                                <div key={i}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700">{item.section}</span>
                                    <span className={`text-sm font-bold ${getScoreColor(parseFloat(item.score))}`}>
                                      {parseFloat(item.score).toFixed(1)} / 10
                                    </span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full ${getProgressBarColor(parseFloat(item.score))}`}
                                      style={{ width: `${(parseFloat(item.score) / 10) * 100}%` }}
                                    ></div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Next Steps */}
                          {assessment.next_steps && assessment.next_steps.length > 0 && (
                            <div className="mt-6 pt-6 border-t">
                              <h4 className="text-lg font-semibold mb-3">Next Steps</h4>
                              <ul className="list-disc list-inside space-y-1">
                                {assessment.next_steps.map((step, i) => (
                                  <li key={i} className="text-sm text-gray-700">{step}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        {activeTab === 'stripe-import' && user?.role === 'admin' && (
          <>
            <StripeImportView />
            <div className="mt-8">
              <BulkUserImportView />
            </div>
          </>
        )}
        
        {activeTab === 'analytics' && user?.role === 'admin' && (
          <AnalyticsView />
        )}
        
        {/* Staff default to assessments if they try to access other tabs */}
        {user?.role === 'staff' && activeTab !== 'assessments' && (
          <div className="text-center py-12">
            <p className="text-gray-600">You only have access to the Assessments tab.</p>
          </div>
        )}
      </div>


      {/* Create User Modal */}
      {showCreateForm && activeTab === 'users' && (
        <CreateUserModal 
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            loadUsers();
          }}
        />
      )}


      {/* Create Video Modal */}
      {showCreateForm && activeTab === 'videos' && (
        <CreateVideoModal 
          categories={categories}
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            loadVideos();
          }}
        />
      )}

      {/* Create Resource Modal */}
      {showCreateForm && activeTab === 'resources' && (
        <CreateResourceModal 
          categories={categories}
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            loadResources();
          }}
        />
      )}

      {/* Create Category Modal */}
      {showCreateForm && activeTab === 'categories' && (
        <CreateCategoryModal 
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setShowCreateForm(false);
            loadCategories();
          }}
        />
      )}

      {/* Edit Modals */}
      {editingItem && activeTab === 'users' && (
        <EditUserModal 
          user={editingItem}
          onClose={() => setEditingItem(null)}
          onSuccess={() => {
            setEditingItem(null);
            loadUsers();
          }}
        />
      )}

      {editingItem && activeTab === 'videos' && (
        <EditVideoModal 
          video={editingItem}
          categories={categories}
          onClose={() => setEditingItem(null)}
          onSuccess={() => {
            setEditingItem(null);
            loadVideos();
          }}
        />
      )}

      {editingItem && activeTab === 'resources' && (
        <EditResourceModal 
          resource={editingItem}
          categories={categories}
          onClose={() => setEditingItem(null)}
          onSuccess={() => {
            setEditingItem(null);
            loadResources();
          }}
        />
      )}

      {editingItem && activeTab === 'categories' && (
        <EditCategoryModal 
          category={editingItem}
          onClose={() => setEditingItem(null)}
          onSuccess={() => {
            setEditingItem(null);
            loadCategories();
          }}
        />
      )}
    </div>
  );
};

// Dashboard Overview Component
const DashboardOverview = ({ data }) => {
  if (!data) return null;

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center">
            <div className="p-2 lg:p-3 bg-blue-100 rounded-xl">
              <Users className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-xl lg:text-2xl font-semibold text-gray-900">{data.users.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center">
            <div className="p-2 lg:p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Active Subscriptions</p>
              <p className="text-xl lg:text-2xl font-semibold text-gray-900">{data.users.active_subscriptions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center">
            <div className="p-2 lg:p-3 bg-purple-100 rounded-xl">
              <Video className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Total Videos</p>
              <p className="text-xl lg:text-2xl font-semibold text-gray-900">{data.content.total_videos}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-all duration-300">
          <div className="flex items-center">
            <div className="p-2 lg:p-3 bg-orange-100 rounded-xl">
              <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-orange-600" />
            </div>
            <div className="ml-3 lg:ml-4">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Total Resources</p>
              <p className="text-xl lg:text-2xl font-semibold text-gray-900">{data.content.total_resources}</p>
            </div>
          </div>
        </div>
      </div>

      {/* User Role Breakdown */}
      <div className="bg-white rounded-2xl shadow-lg">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Users by Role</h3>
        </div>
        <div className="p-4 lg:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {Object.entries(data.users.by_role).map(([role, stats]) => (
              <div key={role} className="text-center p-4 bg-gray-50 rounded-xl">
                <div className="text-xl lg:text-2xl font-bold text-gray-900">{stats.count}</div>
                <div className="text-sm text-gray-600 capitalize">{role.replace('_', ' ')}</div>
                <div className="text-xs text-gray-500">{stats.active_subscriptions} active</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl shadow-lg">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="p-4 lg:p-6">
          <div className="space-y-3 lg:space-y-4">
            {data.recent_activity.slice(0, 5).map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                <div className="flex-shrink-0">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(activity.timestamp).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-xs text-gray-400 capitalize">
                  {activity.activity_type.replace('_', ' ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Users Management Component
const UsersManagement = ({ users, pagination, onLoadUsers, onRefresh, showCreateForm, setShowCreateForm, editingItem, setEditingItem, superAdminStatus, onEditUser, onDeactivateUser, onResetPassword, onDeleteUser, currentUser, advisorInviteStatus, onLoadAdvisorInviteStatus, onSendAdvisorInvite, onResendAdvisorInvite, onSendUserInvite, onLoadUserInviteStatus, userInviteStatus }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // New filter for active status
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // For delete confirmation
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(null); // For deactivate confirmation
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);

  // Load advisor invite status when component mounts - FIXED: Remove function dependency to prevent infinite loop
  useEffect(() => {
    if (onLoadAdvisorInviteStatus) {
      onLoadAdvisorInviteStatus();
    }
  }, []); // Empty dependency array - only run once on mount

  // Load user invite status when component mounts
  useEffect(() => {
    if (onLoadUserInviteStatus) {
      onLoadUserInviteStatus();
    }
  }, []); // Empty dependency array - only run once on mount

  // Load users when component mounts - but only if not already loaded by parent
  useEffect(() => {
    // Only load if users array is empty (parent hasn't loaded yet)
    if (users.length === 0 && onLoadUsers) {
      loadUsersWithFilters(1);
    }
  }, []); // Empty dependency array - only run once on mount

  // Function to load users with current filters (or override with provided values)
  const loadUsersWithFilters = (page = 1, overrideRole = null, overrideStatus = null) => {
    const roleToUse = overrideRole !== null ? overrideRole : roleFilter;
    const statusToUse = overrideStatus !== null ? overrideStatus : statusFilter;
    
    const filters = {
      role: roleToUse && roleToUse !== '' ? roleToUse : undefined,
      is_active: statusToUse === 'all' ? '' : (statusToUse === 'active' ? 'true' : 'false'),
      search: searchTerm && searchTerm.trim() !== '' ? searchTerm : undefined
    };
    console.log('Loading users with filters:', { page, filters, roleFilter, statusFilter, searchTerm, overrideRole, overrideStatus });
    onLoadUsers(page, 50, filters);
    setCurrentPage(page);
  };

  // Refresh handler that maintains current filters and page
  const handleRefresh = () => {
    loadUsersWithFilters(currentPage);
  };

  // Handle search with debouncing
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // Clear existing timer
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    // Set new timer for debounced search
    const timer = setTimeout(() => {
      loadUsersWithFilters(1); // Reset to page 1 when searching
    }, 500); // 500ms delay
    setSearchDebounceTimer(timer);
  };

  // Handle role filter change
  const handleRoleFilterChange = (value) => {
    console.log('Role filter changed to:', value);
    setRoleFilter(value);
    // Pass the new value directly to avoid state update timing issues
    loadUsersWithFilters(1, value, null); // Reset to page 1 when filtering
  };

  // Handle status filter change
  const handleStatusFilterChange = (value) => {
    console.log('Status filter changed to:', value);
    setStatusFilter(value);
    // Pass the new value directly to avoid state update timing issues
    loadUsersWithFilters(1, null, value); // Reset to page 1 when filtering
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (pagination && newPage >= 1 && newPage <= pagination.total_pages) {
      loadUsersWithFilters(newPage);
    } else {
      console.warn('Invalid page change:', newPage, 'pagination:', pagination);
    }
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  // Helper function to get advisor/staff/investor invite status for a user
  const getAdvisorInviteStatus = (user) => {
    if (user.role !== 'advisor' && user.role !== 'staff' && user.role !== 'investor') return null;
    
    const inviteData = advisorInviteStatus.find(invite => invite.email === user.email);
    if (!inviteData) return { status: 'not_invited', statusText: 'Not Invited' };
    
    if (user.is_active) return { status: 'active', statusText: 'Active' };
    if (inviteData.status === 'expired') return { status: 'expired', statusText: 'Expired' };
    if (inviteData.status === 'pending') return { status: 'pending', statusText: 'Pending' };
    
    return { status: 'not_invited', statusText: 'Not Invited' };
  };

  // Helper function to get user invite status for non-advisor/staff/investor users
  const getUserInviteStatus = (user) => {
    if (user.role === 'advisor' || user.role === 'staff' || user.role === 'investor') return null;
    
    // Find the user in the invite status data
    const inviteData = userInviteStatus.find(invite => invite.email === user.email);
    if (!inviteData) return { status: 'not_invited', statusText: 'Not Invited' };
    
    if (user.is_active) return { status: 'active', statusText: 'Active' };
    if (inviteData.status === 'expired') return { status: 'expired', statusText: 'Expired' };
    if (inviteData.status === 'pending') return { status: 'pending', statusText: 'Pending' };
    
    return { status: 'not_invited', statusText: 'Not Invited' };
  };

  // Users are already filtered server-side, so we just use the users array directly

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">User Management</h2>
        <div className="flex space-x-3">
          
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 flex items-center transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Role</label>
            <select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="advisor">Advisor</option>
              <option value="staff">Staff</option>
              <option value="investor">Investor</option>
              <option value="member_paid">Paid Member</option>
              <option value="member_free">Free Member</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <option value="all">All Users</option>
              <option value="active">Active Users</option>
              <option value="inactive">Inactive Users</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            Users ({pagination?.total_users || users.length})
            {pagination && (
              <span className="text-sm text-gray-500 font-normal ml-2">
                (Page {pagination.current_page} of {pagination.total_pages})
                {process.env.NODE_ENV === 'development' && (
                  <span className="ml-2 text-xs">[Debug: total={pagination.total_users}, per_page={pagination.per_page}]</span>
                )}
              </span>
            )}
            {!pagination && (
              <span className="text-sm text-orange-500 font-normal ml-2">[No pagination data]</span>
            )}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invite Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stripe Customer ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription ID</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((userItem) => (
                <tr key={userItem.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {(userItem.first_name || 'U').charAt(0)}{(userItem.last_name || 'U').charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {userItem.first_name || 'No'} {userItem.last_name || 'Name'}
                        </div>
                                                  <div className="text-sm text-gray-500">{userItem.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      userItem.role === 'admin' ? 'bg-red-100 text-red-800' :
                      userItem.role === 'advisor' ? 'bg-purple-100 text-purple-800' :
                      userItem.role === 'staff' ? 'bg-gray-100 text-gray-800' :
                      userItem.role === 'investor' ? 'bg-green-100 text-green-800' :
                      userItem.role === 'member_paid' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {userItem.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      // For paid members, prioritize showing subscription/membership status
                      if (userItem.role === 'member_paid') {
                        if (userItem.subscription_status) {
                          const statusConfig = {
                            'active': { label: 'Active', color: 'bg-green-100 text-green-800' },
                            'trialing': { label: 'Free Trial', color: 'bg-blue-100 text-blue-800' },
                            'past_due': { label: 'Past Due', color: 'bg-yellow-100 text-yellow-800' },
                            'canceled': { label: 'Canceled', color: 'bg-red-100 text-red-800' },
                            'cancelled': { label: 'Canceled', color: 'bg-red-100 text-red-800' },
                            'inactive': { label: 'Inactive', color: 'bg-gray-100 text-gray-800' }
                          };
                          
                          const config = statusConfig[userItem.subscription_status] || { 
                            label: userItem.subscription_status || 'Unknown', 
                            color: 'bg-gray-100 text-gray-800' 
                          };
                          
                          return (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
                              {config.label}
                            </span>
                          );
                        }
                        // If member_paid but no subscription_status, fall back to active/inactive
                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {userItem.is_active ? 'Active' : 'Inactive'}
                          </span>
                        );
                      }
                      
                      // For advisors, use advisor invite status
                      if (userItem.role === 'advisor') {
                        const advisorStatus = getAdvisorInviteStatus(userItem);
                        if (advisorStatus) {
                          const statusColors = {
                            'active': 'bg-green-100 text-green-800',
                            'pending': 'bg-yellow-100 text-yellow-800',
                            'expired': 'bg-red-100 text-red-800',
                            'not_invited': 'bg-gray-100 text-gray-800'
                          };
                          return (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[advisorStatus.status]}`}>
                              {advisorStatus.statusText}
                            </span>
                          );
                        }
                      }
                      
                      // For non-advisor users, use user invite status
                      const userStatus = getUserInviteStatus(userItem);
                      if (userStatus) {
                        const statusColors = {
                          'active': 'bg-green-100 text-green-800',
                          'pending': 'bg-yellow-100 text-yellow-800',
                          'expired': 'bg-red-100 text-red-800',
                          'not_invited': 'bg-orange-100 text-orange-800'
                        };
                        return (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[userStatus.status]}`}>
                            {userStatus.statusText}
                          </span>
                        );
                      }
                      
                      // Fallback to basic active/inactive
                      return (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          userItem.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {userItem.is_active ? 'Active' : 'Inactive'}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {(() => {
                      const inviteStatus = getAdvisorInviteStatus(userItem);
                      if (!inviteStatus) return <span className="text-gray-400">—</span>;
                      
                      const statusColors = {
                        'active': 'bg-green-100 text-green-800',
                        'pending': 'bg-yellow-100 text-yellow-800',
                        'expired': 'bg-red-100 text-red-800',
                        'not_invited': 'bg-gray-100 text-gray-800'
                      };
                      
                      return (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[inviteStatus.status]}`}>
                          {inviteStatus.statusText}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(userItem.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEditUser(userItem)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit user"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => onResetPassword(userItem.id, userItem.email)}
                        className="text-green-600 hover:text-green-900"
                        title="Reset password"
                      >
                        Reset Password
                      </button>
                      {/* Advisor/Staff/Investor invite buttons */}
                      {(userItem.role === 'advisor' || userItem.role === 'staff' || userItem.role === 'investor') && (() => {
                        const inviteStatus = getAdvisorInviteStatus(userItem);
                        if (!inviteStatus) return null;
                        
                        if (inviteStatus.status === 'not_invited') {
                          return (
                            <button
                              onClick={() => onSendAdvisorInvite(userItem.email, `${userItem.first_name} ${userItem.last_name}`.trim(), userItem.company, userItem.role)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Send invite"
                            >
                              Send Invite
                            </button>
                          );
                        } else if (inviteStatus.status === 'expired' || inviteStatus.status === 'pending') {
                          return (
                            <button
                              onClick={() => onResendAdvisorInvite(userItem.email, `${userItem.first_name} ${userItem.last_name}`.trim(), userItem.company, userItem.role)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Resend invite"
                            >
                              Resend Invite
                            </button>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Generic user invite buttons for non-advisor/staff/investor users who need password setup */}
                      {userItem.role !== 'advisor' && userItem.role !== 'staff' && userItem.role !== 'investor' && (() => {
                        const inviteStatus = getUserInviteStatus(userItem);
                        if (!inviteStatus) return null;
                        
                        if (inviteStatus.status === 'not_invited') {
                          return (
                            <button
                              onClick={() => onSendUserInvite(userItem.email, `${userItem.first_name} ${userItem.last_name}`.trim(), userItem.company, userItem.role)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Send password setup email"
                            >
                              Send Invite
                            </button>
                          );
                        } else if (inviteStatus.status === 'expired' || inviteStatus.status === 'pending') {
                          return (
                            <button
                              onClick={() => onSendUserInvite(userItem.email, `${userItem.first_name} ${userItem.last_name}`.trim(), userItem.company, userItem.role)}
                              className="text-purple-600 hover:text-purple-900"
                              title="Resend password setup email"
                            >
                              Resend Invite
                            </button>
                          );
                        }
                        return null;
                      })()}
                      {userItem.email !== currentUser?.email && (
                        <>
                          {userItem.is_active ? (
                            <button 
                              onClick={() => setShowDeactivateConfirm(userItem)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Deactivate user"
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button 
                              onClick={() => onDeactivateUser(userItem.id, true)}
                              className="text-green-600 hover:text-green-900"
                              title="Reactivate user"
                            >
                              Reactivate
                            </button>
                          )}
                          <button 
                            onClick={() => setShowDeleteConfirm(userItem)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete user permanently"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {userItem.stripe_customer_id ? (
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {userItem.stripe_customer_id}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {userItem.subscription_id ? (
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {userItem.subscription_id}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {pagination && pagination.total_pages > 1 ? (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                {Math.min(pagination.current_page * pagination.per_page, pagination.total_users)} of{' '}
                {pagination.total_users} users
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className={`px-3 py-2 text-sm font-medium rounded-xl border transition-all duration-200 flex items-center ${
                  pagination.current_page === 1
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                  let pageNum;
                  if (pagination.total_pages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.current_page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.current_page >= pagination.total_pages - 2) {
                    pageNum = pagination.total_pages - 4 + i;
                  } else {
                    pageNum = pagination.current_page - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-xl border transition-all duration-200 ${
                        pagination.current_page === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
                className={`px-3 py-2 text-sm font-medium rounded-xl border transition-all duration-200 flex items-center ${
                  pagination.current_page === pagination.total_pages
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Deactivate Confirmation Dialog */}
      {showDeactivateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Deactivate User</h3>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Are you sure you want to deactivate <strong>{showDeactivateConfirm.first_name || 'No'} {showDeactivateConfirm.last_name || 'Name'}</strong>?
              </p>
              <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-md">
                <p className="text-sm text-orange-800">
                  <strong>What happens when you deactivate a user:</strong>
                </p>
                <ul className="mt-2 text-sm text-orange-700 list-disc list-inside space-y-1">
                  <li>User cannot login to the system</li>
                  <li>User will be marked as "Inactive" in the user list</li>
                  <li>User data is preserved and can be reactivated</li>
                  <li>User will not receive any notifications</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeactivateConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onDeactivateUser(showDeactivateConfirm.id, false);
                  setShowDeactivateConfirm(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700"
              >
                Deactivate User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete User Permanently</h3>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Are you sure you want to permanently delete <strong>{showDeleteConfirm.first_name || 'No'} {showDeleteConfirm.last_name || 'Name'}</strong>?
              </p>
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-800">
                  <strong>⚠️ WARNING: This action cannot be undone!</strong>
                </p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>All user data will be permanently deleted</li>
                  <li>User progress and activity will be lost</li>
                  <li>User will be completely removed from the system</li>
                  <li>This action cannot be reversed</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onDeleteUser(showDeleteConfirm.id);
                  setShowDeleteConfirm(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Helper function to truncate text for display in admin cards
 * Used to prevent long video descriptions from taking up too much space in the admin interface
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation (default: 120)
 * @returns {string} Truncated text with ellipsis if needed
 */
const truncateText = (text, maxLength = 120) => {
  if (!text) return '';
  return text.length > maxLength 
    ? text.substring(0, maxLength) + '...'
    : text;
};

/**
 * Videos Management Component
 * Displays video cards in a grid layout with truncated descriptions for better admin interface
 * Features:
 * - Grid layout (1 column mobile, 2 tablet, 3 desktop)
 * - Truncated video descriptions (120 chars max) to prevent UI overflow
 * - Edit/Delete buttons for each video
 * - Category and duration display
 */
/**
 * Videos Management Component with Drag-and-Drop Reordering
 * 
 * Features:
 * - Drop zones between videos for intuitive reordering
 * - Visual feedback during drag operations
 * - Sequential sort_order assignment for reliable ordering
 * - Error handling and user feedback
 * 
 * @param {Array} videos - Array of video objects
 * @param {Array} categories - Array of category objects
 * @param {Function} onRefresh - Callback to refresh video list
 * @param {boolean} showCreateForm - Whether to show create video modal
 * @param {Function} setShowCreateForm - Setter for create form visibility
 * @param {Object} editingItem - Currently editing video object
 * @param {Function} setEditingItem - Setter for editing item
 */
const VideosManagement = ({ videos, categories, onRefresh, showCreateForm, setShowCreateForm, editingItem, setEditingItem, activeSubTab, setActiveSubTab }) => {
  const [draggedVideo, setDraggedVideo] = useState(null);
  const [isReordering, setIsReordering] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Filter videos based on active sub-tab
  const filteredVideos = activeSubTab === 'all' 
    ? videos 
    : activeSubTab === 'founder'
    ? videos.filter(v => v.required_role === 'member_paid')
    : videos.filter(v => v.required_role === 'advisor');

  // Handle video deletion
  const handleDeleteVideo = async (videoId, videoTitle) => {
    if (window.confirm(`Are you sure you want to delete "${videoTitle}"? This action cannot be undone.`)) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/videos/${videoId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          alert('Video deleted successfully');
          onRefresh(); // Refresh the video list
        } else {
          const errorData = await response.json();
          alert(`Failed to delete video: ${errorData.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Delete video error:', error);
        alert('Failed to delete video. Please try again.');
      }
    }
  };

  /**
   * Handle drag start - initiates video reordering
   * @param {DragEvent} e - Drag event
   * @param {Object} video - Video object being dragged
   */
  const handleDragStart = (e, video) => {
    setDraggedVideo(video);
    e.dataTransfer.effectAllowed = 'move';
  };

  /**
   * Handle drag over - provides visual feedback for drop zones
   * @param {DragEvent} e - Drag event
   * @param {number} index - Index of the drop zone being hovered
   */
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== undefined && index !== filteredVideos.findIndex(v => v.id === draggedVideo?.id)) {
      setDragOverIndex(index);
    }
  };

  /**
   * Handle drag leave - clears visual feedback when leaving drop zones
   * @param {DragEvent} e - Drag event
   */
  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the drop zone
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  /**
   * Handle drop - reorders videos using sequential sort_order assignment
   * 
   * This function:
   * 1. Reorders the local videos array
   * 2. Assigns sequential sort_order values (1000, 2000, 3000, etc.)
   * 3. Sends batch update to backend API
   * 4. Refreshes the video list on success
   * 
   * @param {DragEvent} e - Drop event
   * @param {number} targetIndex - Index where video should be placed
   */
  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    
    if (!draggedVideo || dragOverIndex === null) {
      setDraggedVideo(null);
      setDragOverIndex(null);
      return;
    }

    const draggedIndex = filteredVideos.findIndex(v => v.id === draggedVideo.id);
    
    if (draggedIndex === -1 || draggedIndex === targetIndex) {
      setDraggedVideo(null);
      setDragOverIndex(null);
      return;
    }

    setIsReordering(true);
    
    try {
      // Create new videos array with reordered items
      const newVideos = [...filteredVideos];
      const [draggedItem] = newVideos.splice(draggedIndex, 1);
      
      // Adjust target index if we're moving down (since we removed an item above)
      const adjustedTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
      newVideos.splice(adjustedTargetIndex, 0, draggedItem);

      // Create new order array with sequential sort_order values
      // Higher numbers = higher priority (appear first)
      // Using 1000-based increments prevents conflicts and allows future insertions
      const videoOrders = newVideos.map((video, index) => ({
        id: video.id,
        sort_order: (newVideos.length - index) * 1000 // Use 1000, 2000, 3000, etc.
      }));

      // Updating video order

      // Update video order via API
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/videos/order`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ videoOrders })
      });

      if (response.ok) {
        // Video order updated successfully
        onRefresh(); // Reload videos with new order
      } else {
        const errorData = await response.json();
        console.error('Failed to update video order:', errorData);
        alert('Failed to update video order. Please try again.');
      }
    } catch (error) {
      console.error('Error updating video order:', error);
      alert('Error updating video order. Please try again.');
    } finally {
      setIsReordering(false);
      setDraggedVideo(null);
      setDragOverIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Video Management</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Video
        </button>
      </div>

      {/* Sub-tabs for filtering by required_role */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab('all')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'all'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All Videos
        </button>
        <button
          onClick={() => setActiveSubTab('founder')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'founder'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Founder Videos
        </button>
        <button
          onClick={() => setActiveSubTab('advisor')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'advisor'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Advisor Videos
        </button>
      </div>

      {/* Videos Grid with Drag & Drop */}
      <div className="space-y-4">
        {filteredVideos.map((video, index) => (
          <div key={`video-${video.id}`}>
            {/* Drop Zone Above Video */}
            <div
              className={`h-2 transition-all duration-200 ${
                dragOverIndex === index ? 'bg-blue-500 h-4 rounded' : 'bg-transparent'
              }`}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            />
            
            {/* Video Card */}
            <div 
              className={`bg-white rounded-lg shadow overflow-hidden cursor-move transition-all duration-200 ${
                draggedVideo?.id === video.id ? 'opacity-50 scale-95 shadow-2xl' : ''
              } ${isReordering ? 'pointer-events-none' : ''}`}
              draggable
              onDragStart={(e) => handleDragStart(e, video)}
            >
            {video.thumbnail_url && (
              <img 
                src={video.thumbnail_url} 
                alt={video.title}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{video.title}</h3>
                <div className="flex items-center text-xs text-gray-400">
                  <span className="mr-2">#{index + 1}</span>
                  <div className="flex flex-col space-y-1">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                  </div>
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">{truncateText(video.description)}</p>
              
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>{video.category_names && video.category_names.length > 0 ? video.category_names[0] : 'Uncategorized'}</span>
                <span>{video.duration_seconds ? `${Math.floor(video.duration_seconds / 60)}:${(video.duration_seconds % 60).toString().padStart(2, '0')}` : 'N/A'}</span>
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingItem(video)}
                  className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
                >
                  Edit
                </button>
                <button 
                  onClick={() => handleDeleteVideo(video.id, video.title)}
                  className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
            </div>
          </div>
        ))}
        
        {/* Final Drop Zone at the end */}
        <div
          className={`h-2 transition-all duration-200 ${
            dragOverIndex === filteredVideos.length ? 'bg-blue-500 h-4 rounded' : 'bg-transparent'
          }`}
          onDragOver={(e) => handleDragOver(e, filteredVideos.length)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, filteredVideos.length)}
        />
      </div>
      
      {isReordering && (
        <div className="text-center text-gray-500 text-sm">
          Updating video order...
        </div>
      )}
    </div>
  );
};

// Resources Management Component
const ResourcesManagement = ({ resources, categories, onRefresh, showCreateForm, setShowCreateForm, editingItem, setEditingItem, activeSubTab, setActiveSubTab }) => {
  // Filter resources based on active sub-tab
  const filteredResources = activeSubTab === 'all' 
    ? resources 
    : activeSubTab === 'founder'
    ? resources.filter(r => r.required_role === 'member_paid')
    : resources.filter(r => r.required_role === 'advisor');

  // Handle resource deletion
  const handleDeleteResource = async (resourceId, resourceTitle) => {
    if (window.confirm(`Are you sure you want to delete "${resourceTitle}"? This action cannot be undone.`)) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/resources/${resourceId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          alert('Resource deleted successfully');
          onRefresh(); // Refresh the resource list
        } else {
          const errorData = await response.json();
          alert(`Failed to delete resource: ${errorData.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Delete resource error:', error);
        alert('Failed to delete resource. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Resource Management</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Resource
        </button>
      </div>

      {/* Sub-tabs for filtering by required_role */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab('all')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'all'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          All Resources
        </button>
        <button
          onClick={() => setActiveSubTab('founder')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'founder'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Founder Resources
        </button>
        <button
          onClick={() => setActiveSubTab('advisor')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeSubTab === 'advisor'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Advisor Resources
        </button>
      </div>

      {/* Resources Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredResources.map((resource) => (
          <div key={resource.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <span className="text-2xl">📄</span>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{resource.title}</h3>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 text-xs rounded ${
                    resource.content_type === 'guide' ? 'bg-purple-100 text-purple-800' :
                    resource.content_type === 'file' ? 'bg-blue-100 text-blue-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {resource.content_type || 'link'}
                  </span>
                  <p className="text-sm text-gray-500">{resource.file_type}</p>
                </div>
              </div>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">{truncateText(resource.description)}</p>
            
            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <span>{resource.category_names && resource.category_names.length > 0 ? resource.category_names[0] : 'Uncategorized'}</span>
              <span>{resource.file_size_bytes ? `${(resource.file_size_bytes / 1024 / 1024).toFixed(1)} MB` : 'N/A'}</span>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setEditingItem(resource)}
                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
              >
                Edit
              </button>
              <button 
                onClick={() => handleDeleteResource(resource.id, resource.title)}
                className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Categories Management Component
const CategoriesManagement = ({ categories, onRefresh, showCreateForm, setShowCreateForm, editingItem, setEditingItem }) => {
  // Handle category deletion
  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (window.confirm(`Are you sure you want to delete the category "${categoryName}"? This action cannot be undone.`)) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/categories/${categoryId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          alert('Category deleted successfully');
          onRefresh(); // Refresh the category list
        } else {
          const errorData = await response.json();
          alert(`Failed to delete category: ${errorData.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Delete category error:', error);
        alert('Failed to delete category. Please try again.');
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Category Management</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Category
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => (
          <div key={category.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-4">
              <div 
                className="w-4 h-4 rounded-full mr-3"
                style={{ backgroundColor: category.color }}
              ></div>
              <h3 className="text-lg font-semibold text-gray-900">{category.name}</h3>
            </div>
            
            <p className="text-gray-600 text-sm mb-4">{truncateText(category.description)}</p>
            
            <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
              <span>Order: {category.sort_order}</span>
              <span>ID: {category.id}</span>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setEditingItem(category)}
                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
              >
                Edit
              </button>
              <button 
                onClick={() => handleDeleteCategory(category.id, category.name)}
                className="flex-1 bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Assessment Import View Component
const AssessmentImportView = ({ onImportComplete }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [csvPreview, setCsvPreview] = useState(null);
  const fileInputRef = React.useRef(null);

  // Enhanced CSV parser that handles quoted fields with line breaks
  const parseCSV = (csvText) => {
    const lines = [];
    let currentLine = '';
    let inQuotes = false;
    
    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      const nextChar = csvText[i + 1];
      
      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentLine += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
          currentLine += char;
        }
      } else if (char === '\n' && !inQuotes) {
        lines.push(currentLine);
        currentLine = '';
      } else {
        currentLine += char;
      }
    }
    if (currentLine) lines.push(currentLine);

    if (lines.length < 2) {
      throw new Error('CSV must have at least a header row and one data row');
    }

    // Parse header
    const headers = parseCSVLine(lines[0]);
    
    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) {
        console.warn(`Row ${i + 1} has ${values.length} columns, expected ${headers.length}. Skipping.`);
        continue;
      }
      
      const row = {};
      headers.forEach((header, index) => {
        row[header.trim()] = values[index]?.trim() || '';
      });
      
      // Only add non-empty rows
      if (Object.values(row).some(val => val)) {
        data.push(row);
      }
    }
    
    return data;
  };

  // Parse a single CSV line, handling quoted fields
  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    values.push(current);
    return values;
  };

  // Calculate section scores from individual question answers
  const calculateSectionScores = (row) => {
    const sections = [];
    
    for (let i = 1; i <= 12; i++) {
      const sectionNum = i;
      const sectionTitle = row[`section_${sectionNum}_title`] || row[`section ${sectionNum}_title`];
      
      if (!sectionTitle) continue;
      
      const answers = [];
      for (let q = 1; q <= 6; q++) {
        const answer = row[`section${sectionNum}_${q}_answer`] || 
                     row[`section ${sectionNum}_${q}_answer`];
        
        if (answer && answer.trim() && !isNaN(parseFloat(answer))) {
          answers.push(parseFloat(answer));
        }
      }
      
      if (answers.length > 0) {
        const avgScore = answers.reduce((sum, val) => sum + val, 0) / answers.length;
        sections.push({
          section: sectionTitle.trim(),
          score: parseFloat(avgScore.toFixed(2))
        });
      }
    }
    
    return sections;
  };

  // Build responses object from individual answer columns
  const buildResponses = (row) => {
    const responses = {};
    
    for (let sectionNum = 1; sectionNum <= 12; sectionNum++) {
      for (let q = 1; q <= 6; q++) {
        const answer = row[`section${sectionNum}_${q}_answer`] || 
                     row[`section ${sectionNum}_${q}_answer`];
        
        if (answer && answer.trim() && !isNaN(parseFloat(answer))) {
          const key = `${sectionNum - 1}-${q - 1}`;
          responses[key] = parseInt(answer);
        }
      }
    }
    
    return responses;
  };

  // Collect next steps from multiple columns
  const collectNextSteps = (row) => {
    const nextSteps = [];
    
    for (let i = 1; i <= 10; i++) {
      const stepKey = i === 1 ? 'Next Step' : `Next Step ${i}`;
      const step = row[stepKey];
      
      if (step && step.trim()) {
        const cleanStep = step.trim()
          .replace(/\n/g, ' ')
          .replace(/^["']|["']$/g, '')
          .trim();
        
        if (cleanStep && cleanStep.length > 0) {
          nextSteps.push(cleanStep);
        }
      }
    }
    
    return nextSteps;
  };

  // Parse date from various formats
  const parseDate = (dateStr) => {
    if (!dateStr) return new Date().toISOString();
    
    let parsedDate;
    
    // Format: 9/11/2025 or 12/10/2025 (M/D/YYYY)
    if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      const [month, day, year] = dateStr.split('/');
      parsedDate = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    }
    // Format: 2025-12-05 (YYYY-MM-DD)
    else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      parsedDate = new Date(dateStr);
    }
    // Format: 02/10/2025 (DD/MM/YYYY - European)
    else if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateStr.split('/');
      parsedDate = new Date(`${year}-${month}-${day}`);
    }
    else {
      parsedDate = new Date(dateStr);
    }
    
    if (isNaN(parsedDate.getTime())) {
      return new Date().toISOString();
    }
    
    return parsedDate.toISOString();
  };

  // Convert CSV row to assessment format
  const convertCsvToAssessmentFormat = (csvData) => {
    return csvData.map(row => {
      const email = row.Email || row.email;
      const firstName = row['First Name'] || row.first_name || row.firstName || '';
      const lastName = row['Last Name'] || row.last_name || row.lastName || '';
      const dateStr = row['Assessment Date:'] || row['Assessment Date'] || row.date || '';
      const completedAt = parseDate(dateStr);
      const assessmentDate = completedAt.split('T')[0];
      
      const sectionScores = calculateSectionScores(row);
      const responses = buildResponses(row);
      const nextSteps = collectNextSteps(row);
      
      return {
        email: email?.trim().toLowerCase() || '', // Normalize to lowercase for case-insensitive matching
        first_name: firstName?.trim() || '', // Include for name matching fallback
        last_name: lastName?.trim() || '', // Include for name matching fallback
        overall_score: parseFloat(row.overall_score || 0),
        readiness_verdict: row.readiness_verdict || 'Unknown',
        verdict_description: row.verdict_description || null,
        section_scores: sectionScores,
        responses: responses,
        next_steps: nextSteps,
        phone: row['Phone number'] || row.phone || null,
        annual_revenue: row['Annual Revenue:'] || row['Annual Revenue'] || null,
        industry: row.Industry || row.industry || null,
        fundraising_status: row['Fundraising Status'] || row.fundraising_status || null,
        total_questions: parseInt(row['Total Questions:'] || row['Total Questions'] || Object.keys(responses).length),
        assessment_date: assessmentDate,
        completed_at: completedAt
      };
    });
  };

  // Handle CSV file upload
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setCsvFile(file);
      setError('');
      setResult(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          const parsedData = parseCSV(csvText);
          setCsvPreview(parsedData.slice(0, 3)); // Show first 3 rows
        } catch (err) {
          setError(`Error parsing CSV: ${err.message}`);
          setCsvPreview(null);
        }
      };
      reader.readAsText(file);
    } else {
      setError('Please select a valid CSV file');
    }
  };

  const handleImport = async () => {
    if (!csvFile) {
      setError('Please select a CSV file to import');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResult(null);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          const csvData = parseCSV(csvText);
          
          if (csvData.length === 0) {
            setError('CSV file is empty');
            setLoading(false);
            return;
          }

          const assessments = convertCsvToAssessmentFormat(csvData);
          
          // Continue with import
          await performImport(assessments);
        } catch (error) {
          setError(`Error processing CSV: ${error.message}`);
          setLoading(false);
        }
      };
      reader.readAsText(csvFile);
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Assessment import error:', error);
      setLoading(false);
    }
  };

  const performImport = async (assessments) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/assessment/admin/bulk-import`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ assessments })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data.data);
        if (onImportComplete) {
          onImportComplete();
        }
      } else {
        setError(data.message || data.error || 'Import failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Assessment import error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Import Assessments from CSV</h3>
          <p className="text-sm text-gray-600">Upload a CSV file with assessment data</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleCsvUpload}
            className="hidden"
            id="assessment-csv-upload"
          />
          <label
            htmlFor="assessment-csv-upload"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
          >
            <Upload className="w-4 h-4 mr-2" />
            {csvFile ? csvFile.name : 'Select CSV File'}
          </label>
        </div>

        {csvPreview && (
          <div className="bg-gray-50 rounded p-4">
            <p className="text-sm font-medium mb-2">Preview (first 3 rows):</p>
            <div className="text-xs text-gray-600">
              <p>Total rows: {csvPreview.length}</p>
              <p className="mt-1">Sample email: {csvPreview[0]?.Email || csvPreview[0]?.email || 'N/A'}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {result && (
          <div className="bg-green-50 border border-green-200 rounded p-4">
            <p className="text-sm font-medium text-green-800 mb-2">Import Complete!</p>
            <div className="text-sm text-green-700">
              <p>✅ Successfully imported: {result.imported}</p>
              <p>⚠️ Skipped/Errors: {result.skipped}</p>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Errors:</p>
                  <ul className="list-disc list-inside mt-1">
                    {result.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>{err.email}: {err.error}</li>
                    ))}
                    {result.errors.length > 5 && <li>... and {result.errors.length - 5} more</li>}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        <button
          onClick={handleImport}
          disabled={!csvFile || loading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {loading ? 'Importing...' : 'Import Assessments'}
        </button>
      </div>
    </div>
  );
};

// Stripe Import View Component
const StripeImportView = () => {
  const [importData, setImportData] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [importMode, setImportMode] = useState('json'); // 'json' or 'csv'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [csvPreview, setCsvPreview] = useState(null);

  // CSV parsing function
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }
    return data;
  };

  // Handle CSV file upload
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvText = e.target.result;
        const parsedData = parseCSV(csvText);
        setCsvPreview(parsedData.slice(0, 5)); // Show first 5 rows
        setError('');
      };
      reader.readAsText(file);
    } else {
      setError('Please select a valid CSV file');
    }
  };

  // Convert CSV data to Stripe format
  const convertCsvToStripeFormat = (csvData) => {
    return csvData.map(row => ({
      object: 'subscription',
      id: row['id'], // Fixed: use lowercase 'id' to match CSV header
      customer: row['Customer ID'],
      status: row['Status'].toLowerCase(),
      current_period_start: new Date(row['Current Period Start (UTC)']).getTime() / 1000,
      current_period_end: new Date(row['Current Period End (UTC)']).getTime() / 1000,
      // Add customer details for better user creation
      customer_email: row['Customer Email'],
      customer_name: row['Customer Name']
    }));
  };

  const handleImport = async () => {
    if (importMode === 'json' && !importData.trim()) {
      setError('Please paste Stripe data to import');
      return;
    }

    if (importMode === 'csv' && !csvFile) {
      setError('Please select a CSV file to import');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResult(null);

      let stripeCustomers = [];

      if (importMode === 'json') {
        // Parse JSON to validate format
        let parsedData;
        try {
          parsedData = JSON.parse(importData);
        } catch (parseError) {
          setError('Invalid JSON format. Please check your data and try again.');
          setLoading(false);
          return;
        }
        stripeCustomers = [parsedData];
      } else if (importMode === 'csv') {
        // Parse CSV file
        const reader = new FileReader();
        reader.onload = async (e) => {
          const csvText = e.target.result;
          const csvData = parseCSV(csvText);
          stripeCustomers = convertCsvToStripeFormat(csvData);
          
          // Continue with import
          await performImport(stripeCustomers);
        };
        reader.readAsText(csvFile);
        return; // Exit early, will continue in reader.onload
      }

      await performImport(stripeCustomers);
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Stripe import error:', error);
      setLoading(false);
    }
  };

  const performImport = async (stripeCustomers) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/stripe/import-users`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stripe_customers: stripeCustomers })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data.data); // Backend returns { success: true, data: results }
      } else {
        setError(data.message || 'Import failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Stripe import error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Stripe Import</h2>
        <div className="flex items-center space-x-2">
          <Upload className="w-5 h-5 text-blue-600" />
          <span className="text-sm text-gray-600">Import existing customers</span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Stripe Data</h3>
            <p className="text-gray-600 mb-4">
              Import existing Stripe subscribers using CSV export or individual JSON objects.
            </p>
          </div>

          {/* Import Mode Selection */}
          <div className="flex space-x-4">
            <button
              onClick={() => setImportMode('csv')}
              className={`px-4 py-2 rounded-lg font-medium ${
                importMode === 'csv'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              CSV Import (Recommended)
            </button>
            <button
              onClick={() => setImportMode('json')}
              className={`px-4 py-2 rounded-lg font-medium ${
                importMode === 'json'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              JSON Import (Testing)
            </button>
          </div>

          {/* CSV Import Interface */}
          {importMode === 'csv' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CSV File Upload
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-sm text-gray-500 mt-1">
                Upload the CSV export from Stripe Dashboard → Subscriptions
              </p>
              
              {/* CSV Preview */}
              {csvPreview && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Preview (First 5 rows):</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(csvPreview[0] || {}).map((key) => (
                            <th key={key} className="px-2 py-1 text-left border-b">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, i) => (
                              <td key={i} className="px-2 py-1 border-b">
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* JSON Import Interface */}
          {importMode === 'json' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stripe Data (JSON)
              </label>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="Paste Stripe webhook data here..."
                className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                {error}
              </div>
            </div>
          )}

          {result && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 mr-2" />
                Import completed successfully!
              </div>
              <div className="text-sm">
                <p>Users created: {result.created || 0}</p>
                <p>Users updated: {result.updated || 0}</p>
                <p>Errors: {result.errors?.length || 0}</p>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={handleImport}
              disabled={loading || (importMode === 'json' && !importData.trim()) || (importMode === 'csv' && !csvFile)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Import Data</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                setImportData('');
                setCsvFile(null);
                setCsvPreview(null);
                setResult(null);
                setError('');
              }}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Import Methods:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>CSV Import (Recommended):</strong> Bulk import all active subscribers from Stripe CSV export</li>
          <li>• <strong>JSON Import (Testing):</strong> Import individual subscription objects for testing</li>
        </ul>
        <p className="text-xs text-blue-700 mt-2">
          CSV import is perfect for one-time bulk import of existing subscribers. JSON import is useful for testing individual subscriptions.
        </p>
      </div>
    </div>
  );
};

// Bulk User Import View Component
const BulkUserImportView = () => {
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [csvPreview, setCsvPreview] = useState(null);
  const fileInputRef = React.useRef(null);

  // CSV parsing function
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }
    return data;
  };

  // Handle CSV file upload
  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvText = e.target.result;
        const parsedData = parseCSV(csvText);
        
        // Validate CSV structure
        const validation = validateCsvStructure(parsedData);
        if (!validation.isValid) {
          setError(validation.error);
          setCsvPreview(null);
        } else {
          setCsvPreview(parsedData.slice(0, 5)); // Show first 5 rows
          setError('');
        }
      };
      reader.readAsText(file);
    } else {
      setError('Please select a valid CSV file');
    }
  };

  // Validate CSV structure
  const validateCsvStructure = (csvData) => {
    if (csvData.length === 0) {
      return { isValid: false, error: 'CSV file is empty' };
    }

    // Check required columns (case-insensitive)
    const firstRow = csvData[0];
    const keys = Object.keys(firstRow).map(k => k.toLowerCase());
    
    const hasEmail = keys.some(k => k === 'email');
    const hasRole = keys.some(k => k === 'role');
    
    // Check for name field (either 'name' or both 'first_name' and 'last_name')
    const hasName = keys.some(k => k === 'name');
    const hasFirstName = keys.some(k => k === 'first_name');
    const hasLastName = keys.some(k => k === 'last_name');
    const hasNameFields = hasName || (hasFirstName && hasLastName);

    if (!hasEmail) {
      return { isValid: false, error: 'CSV must contain an "email" column' };
    }
    if (!hasRole) {
      return { isValid: false, error: 'CSV must contain a "role" column' };
    }
    if (!hasNameFields) {
      return { isValid: false, error: 'CSV must contain either a "name" column or both "first_name" and "last_name" columns' };
    }

    // Validate role values
    const validRoles = ['advisor', 'investor', 'member_free', 'member_paid', 'staff', 'admin'];
    const invalidRoles = [];
    csvData.forEach((row, index) => {
      const roleKey = Object.keys(row).find(k => k.toLowerCase() === 'role');
      if (roleKey) {
        const role = row[roleKey].toLowerCase().trim();
        if (role && !validRoles.includes(role)) {
          invalidRoles.push(`Row ${index + 2}: Invalid role "${row[roleKey]}"`);
        }
      }
    });

    if (invalidRoles.length > 0) {
      return { 
        isValid: false, 
        error: `Invalid roles found:\n${invalidRoles.slice(0, 5).join('\n')}${invalidRoles.length > 5 ? '\n...' : ''}` 
      };
    }

    return { isValid: true };
  };

  // Convert CSV data to user format
  const convertCsvToUserFormat = (csvData) => {
    return csvData.map(row => {
      // Find column keys (case-insensitive)
      const emailKey = Object.keys(row).find(k => k.toLowerCase() === 'email');
      const roleKey = Object.keys(row).find(k => k.toLowerCase() === 'role');
      const nameKey = Object.keys(row).find(k => k.toLowerCase() === 'name');
      const firstNameKey = Object.keys(row).find(k => k.toLowerCase() === 'first_name');
      const lastNameKey = Object.keys(row).find(k => k.toLowerCase() === 'last_name');

      const email = row[emailKey]?.trim() || '';
      const role = row[roleKey]?.toLowerCase().trim() || 'member_free';

      let first_name = null;
      let last_name = null;

      if (nameKey && row[nameKey]) {
        // Split full name
        const nameParts = row[nameKey].trim().split(' ');
        first_name = nameParts[0] || null;
        last_name = nameParts.slice(1).join(' ') || null;
      } else if (firstNameKey || lastNameKey) {
        first_name = row[firstNameKey]?.trim() || null;
        last_name = row[lastNameKey]?.trim() || null;
      }

      return {
        email,
        role,
        first_name,
        last_name
      };
    });
  };

  const handleImport = async () => {
    if (!csvFile) {
      setError('Please select a CSV file to import');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResult(null);

      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvText = e.target.result;
          const csvData = parseCSV(csvText);
          
          // Validate again before import
          const validation = validateCsvStructure(csvData);
          if (!validation.isValid) {
            setError(validation.error);
            setLoading(false);
            return;
          }

          const users = convertCsvToUserFormat(csvData);
          
          // Continue with import
          await performImport(users);
        } catch (error) {
          setError(`Error processing CSV: ${error.message}`);
          setLoading(false);
        }
      };
      reader.readAsText(csvFile);
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Bulk user import error:', error);
      setLoading(false);
    }
  };

  const performImport = async (users) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/bulk-import`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ users })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult(data.data); // Backend returns { success: true, data: results }
      } else {
        setError(data.message || 'Import failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Bulk user import error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Bulk User Import</h2>
        <div className="flex items-center space-x-2">
          <Upload className="w-5 h-5 text-blue-600" />
          <span className="text-sm text-gray-600">Import advisors, investors, and free members</span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Users from CSV</h3>
            <p className="text-gray-600 mb-4">
              Bulk import advisors, investors, or free members by uploading a CSV file with name, email, and role.
              These users will be created without Stripe data and ready to receive invite emails.
            </p>
          </div>

          {/* CSV Import Interface */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CSV File Upload
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleCsvUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">
              CSV must include: <strong>email</strong>, <strong>role</strong>, and either <strong>name</strong> (or <strong>first_name</strong> and <strong>last_name</strong>)
            </p>
            
            {/* CSV Preview */}
            {csvPreview && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Preview (First 5 rows):</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {Object.keys(csvPreview[0] || {}).map((key) => (
                          <th key={key} className="px-2 py-1 text-left border-b">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, index) => (
                        <tr key={index}>
                          {Object.values(row).map((value, i) => (
                            <td key={i} className="px-2 py-1 border-b">
                              {value}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg whitespace-pre-line">
              <div className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {result && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 mr-2" />
                Import completed successfully!
              </div>
              <div className="text-sm">
                <p>Users created: {result.created || 0}</p>
                <p>Users skipped (already exist): {result.skipped || 0}</p>
                <p>Errors: {result.errors?.length || 0}</p>
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="font-semibold">Error details:</p>
                    <ul className="list-disc list-inside">
                      {result.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>{err.email || err.user || 'unknown'}: {err.error}</li>
                      ))}
                      {result.errors.length > 10 && <li>...and {result.errors.length - 10} more</li>}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={handleImport}
              disabled={loading || !csvFile}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Import Users</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => {
                setCsvFile(null);
                setCsvPreview(null);
                setResult(null);
                setError('');
                // Reset file input
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">CSV Format:</h4>
        <p className="text-sm text-blue-800 mb-2">
          Your CSV file should include the following columns:
        </p>
        <ul className="text-sm text-blue-800 space-y-1 mb-2">
          <li>• <strong>email</strong> (required) - User email address</li>
          <li>• <strong>role</strong> (required) - One of: advisor, investor, member_free, member_paid, staff, admin</li>
          <li>• <strong>name</strong> (optional) - Full name (will be split into first/last)</li>
          <li>• <strong>first_name</strong> (optional) - First name (use with last_name)</li>
          <li>• <strong>last_name</strong> (optional) - Last name (use with first_name)</li>
        </ul>
        <p className="text-xs text-blue-700 mt-2">
          Users will be created without passwords and will need to set up their password via invite email. 
          They will have the specified role assigned. No Stripe data will be associated with these users.
        </p>
      </div>
    </div>
  );
};

// Analytics View Component
const AnalyticsView = () => {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Analytics</h2>
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-600">Analytics dashboard coming soon...</p>
      </div>
    </div>
  );
};

  // Edit User Modal Component
  const EditUserModal = ({ user, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      role: user?.role || 'member_free',
      subscription_status: user?.subscription_status || 'inactive',
      phone: user?.phone || '',
      company: user?.company || '',
      position: user?.position || ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users/${user.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update user');
        }

        onSuccess();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        
        // Auto-set subscription status based on role
        if (name === 'role') {
          if (value === 'advisor' || value === 'admin') {
            newData.subscription_status = 'active'; // Free access
          } else if (value === 'member_free') {
            newData.subscription_status = 'active'; // Free access to free content
          } else if (value === 'member_paid') {
            newData.subscription_status = 'inactive'; // Needs subscription
          }
        }
        
        return newData;
      });
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit User</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="member_free">Free Member</option>
                  <option value="member_paid">Paid Member</option>
                  <option value="advisor">Advisor</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                <select
                  name="subscription_status"
                  value={formData.subscription_status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="inactive">Inactive</option>
                  <option value="active">Active (Free/Paid)</option>
                  <option value="trialing">Trialing</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="past_due">Past Due</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.role === 'advisor' || formData.role === 'admin' 
                    ? 'Advisors and admins get free access (Active status)'
                    : formData.role === 'member_free'
                    ? 'Free members get access to free content (Active status)'
                    : 'Paid members need active subscription for full access'
                  }
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Updating...' : 'Update User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };


  /**
   * CreateVideoModal Component
   * 
   * Modal for creating new videos with support for:
   * - Video metadata (title, description, Vimeo URL, dates, duration)
   * - Category assignment (multiple categories supported)
   * - Content linking (prompts, resources, external URLs)
   * - Access control (required_role determines who can view the video)
   * 
   * @param {Array} categories - Available categories for selection
   * @param {Function} onClose - Callback to close the modal
   * @param {Function} onSuccess - Callback when video is successfully created
   */
  const CreateVideoModal = ({ categories, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      vimeo_url: 'https://player.vimeo.com/video/',
      training_date: '',
      duration_seconds: null,
      category_ids: [],
      required_role: 'member_paid',
      is_featured: false,
      content_links: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Content data for linking
    const [videos, setVideos] = useState([]);
    const [resources, setResources] = useState([]);
    const [prompts, setPrompts] = useState([]);
    const [contentLoading, setContentLoading] = useState(false);

    /**
     * Load available resources and prompts for content linking
     * Fetches data in parallel when modal opens to populate dropdown menus
     */
    const loadContentData = useCallback(async () => {
      try {
        setContentLoading(true);
        const token = localStorage.getItem('authToken');
        
        // Load resources and prompts in parallel
        const [resourcesResponse, promptsResponse] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/api/admin/resources`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (resourcesResponse.ok) {
          const resourcesData = await resourcesResponse.json();
          setResources(resourcesData.data.resources || []);
        }

        if (promptsResponse.ok) {
          const promptsData = await promptsResponse.json();
          setPrompts(promptsData.data || []);
        }
      } catch (error) {
        console.error('Error loading content data:', error);
      } finally {
        setContentLoading(false);
      }
    }, []);

    // Load content data when modal opens
    useEffect(() => {
      loadContentData();
    }, [loadContentData]);

    /**
     * Add a new content link to the video
     * Creates a new link entry with default values for linking prompts, resources, or external URLs
     */
    const addContentLink = () => {
      setFormData(prev => ({
        ...prev,
        content_links: [...prev.content_links, {
          link_type: 'prompt',
          content_id: '',
          external_url: '',
          link_text: '',
          description: '',
          display_order: prev.content_links.length
        }]
      }));
    };

    /**
     * Update a specific field in a content link
     * @param {number} index - Index of the link to update
     * @param {string} field - Field name to update (link_type, content_id, external_url, link_text, description)
     * @param {any} value - New value for the field
     */
    const updateContentLink = (index, field, value) => {
      setFormData(prev => ({
        ...prev,
        content_links: prev.content_links.map((link, i) => 
          i === index ? { ...link, [field]: value } : link
        )
      }));
    };

    /**
     * Remove a content link from the video
     * @param {number} index - Index of the link to remove
     */
    const removeContentLink = (index) => {
      setFormData(prev => ({
        ...prev,
        content_links: prev.content_links.filter((_, i) => i !== index)
      }));
    };

    /**
     * Handle form submission for video creation
     * Sends video data including metadata, categories, and content links to backend
     */
    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/videos`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create video');
        }

        onSuccess();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setFormData(prev => ({
        ...prev,
        [e.target.name]: value
      }));
    };

    // Add error boundary for the modal
    if (!categories) {
      return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Video</h3>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="text-center py-8">
                <p className="text-red-600">Error: Categories not loaded. Please try again.</p>
                <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Video</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm mb-4">
                {error}
              </div>
            )}
            {contentLoading && (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading content...</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vimeo URL *</label>
                <input
                  type="url"
                  name="vimeo_url"
                  value={formData.vimeo_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Date</label>
                <input
                  type="date"
                  name="training_date"
                  value={formData.training_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Date when the training session was conducted</p>
              </div>
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    name="duration_seconds"
                    value={formData.duration_seconds ? Math.floor(formData.duration_seconds / 60) : ''}
                    onChange={(e) => {
                      const minutes = parseInt(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        duration_seconds: minutes > 0 ? minutes * 60 : null
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter duration in minutes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                  <select
                    name="category_ids"
                    multiple
                    value={formData.category_ids || []}
                    onChange={(e) => {
                      const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({
                        ...prev,
                        category_ids: selectedIds
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories && categories.length > 0 ? (
                      categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))
                    ) : (
                      <option value="" disabled>No categories available</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple categories</p>
                </div>
              </div>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Featured Video</span>
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content Visibility *</label>
                <select
                  name="required_role"
                  value={formData.required_role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="member_paid">Founders (appears in main Videos section)</option>
                  <option value="advisor">Advisors Only (appears in Advisor Hub only)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Advisors can still access Founder content</p>
              </div>

              {/* Content Linking Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Content Links</h4>
                  <button
                    type="button"
                    onClick={addContentLink}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Link</span>
                  </button>
                </div>

                {formData.content_links.map((link, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-gray-900">Link {index + 1}</h5>
                      <button
                        type="button"
                        onClick={() => removeContentLink(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link Type *</label>
                        <select
                          value={link.link_type}
                          onChange={(e) => updateContentLink(index, 'link_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="prompt">AI Prompt</option>
                          <option value="resource">Resource</option>
                          <option value="external">External Link</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link Text *</label>
                        <input
                          type="text"
                          value={link.link_text}
                          onChange={(e) => updateContentLink(index, 'link_text', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Use: Business Analysis Prompt"
                        />
                      </div>
                    </div>

                    {link.link_type === 'prompt' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Prompt *</label>
                        <select
                          value={link.content_id}
                          onChange={(e) => updateContentLink(index, 'content_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Choose a prompt...</option>
                          {prompts.map((prompt) => (
                            <option key={prompt.id} value={prompt.id}>
                              {prompt.is_multi_part ? `${prompt.title} (${prompt.total_parts} parts)` : prompt.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {link.link_type === 'resource' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Resource *</label>
                        <select
                          value={link.content_id}
                          onChange={(e) => updateContentLink(index, 'content_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Choose a resource...</option>
                          {resources.map((resource) => (
                            <option key={resource.id} value={resource.id}>
                              {resource.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {link.link_type === 'external' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">External URL *</label>
                        <input
                          type="url"
                          value={link.external_url}
                          onChange={(e) => updateContentLink(index, 'external_url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://example.com"
                        />
                      </div>
                    )}

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={link.description}
                        onChange={(e) => updateContentLink(index, 'description', e.target.value)}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Optional description of how this relates to the video"
                      />
                    </div>
                  </div>
                ))}

                {formData.content_links && formData.content_links.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Link className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>No content links added yet.</p>
                    <p className="text-sm">Click "Add Link" to connect prompts or resources to this video.</p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Creating...' : 'Create Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  /**
   * EditVideoModal Component
   * 
   * Modal for editing existing videos with support for:
   * - Video metadata updates
   * - Category assignment (multiple categories supported)
   * - Content linking (add/remove/edit links to prompts, resources, external URLs)
   * - Access control updates
   * 
   * Pre-populates form with existing video data including content links.
   * Content links section is always visible (even if empty) to allow adding links
   * to videos created without them.
   * 
   * @param {Object} video - The video object being edited (includes content_links array)
   * @param {Array} categories - Available categories for selection
   * @param {Function} onClose - Callback to close the modal
   * @param {Function} onSuccess - Callback when video is successfully updated
   */
  const EditVideoModal = ({ video, categories, onClose, onSuccess }) => {
    /**
     * Format training_date for HTML date input (YYYY-MM-DD format)
     * Handles date-only strings without timezone conversion to prevent day shifts
     * 
     * PostgreSQL DATE columns may be returned as Date objects or ISO timestamps.
     * We extract the date portion directly from ISO strings (YYYY-MM-DDTHH:mm:ss.sssZ)
     * to avoid timezone conversion issues.
     * 
     * @param {string|Date} dateValue - Date from database (YYYY-MM-DD, ISO string, or Date object)
     * @returns {string} Formatted date string in YYYY-MM-DD format
     */
    const formatDateForInput = (dateValue) => {
      if (!dateValue) return '';
      
      // Handle Date objects by converting to ISO string first
      if (dateValue instanceof Date) {
        dateValue = dateValue.toISOString();
      }
      
      // Convert to string if not already
      const dateString = String(dateValue);
      
      // If it's already a date-only string (YYYY-MM-DD), return as-is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }
      
      // If it's an ISO timestamp (YYYY-MM-DDTHH:mm:ss...), extract just the date part
      // This prevents timezone conversion - we use the date as stored, not converted
      const isoMatch = dateString.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoMatch) {
        return isoMatch[1];
      }
      
      // Fallback: parse as Date but use UTC methods to avoid timezone shift
      try {
        const date = new Date(dateString);
        // Use UTC methods to get the exact date without timezone conversion
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        console.error('Error parsing date:', dateString, error);
        return '';
      }
    };

    const [formData, setFormData] = useState({
      title: video?.title || '',
      description: video?.description || '',
      vimeo_url: video?.vimeo_url || '',
      training_date: formatDateForInput(video?.training_date),
      duration_seconds: video?.duration_seconds || '',
      category_ids: video?.category_ids || [],
      is_featured: video?.is_featured || false,
      content_links: video?.content_links || []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Content data for linking
    const [videos, setVideos] = useState([]);
    const [resources, setResources] = useState([]);
    const [prompts, setPrompts] = useState([]);
    const [contentLoading, setContentLoading] = useState(false);

    const loadContentData = useCallback(async () => {
      try {
        setContentLoading(true);
        const token = localStorage.getItem('authToken');
        
        // Load resources and prompts in parallel
        const [resourcesResponse, promptsResponse] = await Promise.all([
          fetch(`${import.meta.env.VITE_API_URL}/api/admin/resources`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts`, {
            credentials: 'include',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (resourcesResponse.ok) {
          const resourcesData = await resourcesResponse.json();
          setResources(resourcesData.data.resources || []);
        }

        if (promptsResponse.ok) {
          const promptsData = await promptsResponse.json();
          setPrompts(promptsData.data || []);
        }
      } catch (error) {
        console.error('Error loading content data:', error);
      } finally {
        setContentLoading(false);
      }
    }, []);

    // Load content data when modal opens
    useEffect(() => {
      loadContentData();
    }, [loadContentData]);

    /**
     * Add a new content link to the video
     * Creates a new link entry with default values for linking prompts, resources, or external URLs
     * This allows adding links to videos that were created without any content links
     */
    const addContentLink = () => {
      setFormData(prev => ({
        ...prev,
        content_links: [...prev.content_links, {
          link_type: 'prompt',
          content_id: '',
          external_url: '',
          link_text: '',
          description: '',
          display_order: prev.content_links.length
        }]
      }));
    };

    /**
     * Update a specific field in a content link
     * @param {number} index - Index of the link to update
     * @param {string} field - Field name to update (link_type, content_id, external_url, link_text, description)
     * @param {any} value - New value for the field
     */
    const updateContentLink = (index, field, value) => {
      setFormData(prev => ({
        ...prev,
        content_links: prev.content_links.map((link, i) => 
          i === index ? { ...link, [field]: value } : link
        )
      }));
    };

    /**
     * Remove a content link from the video
     * @param {number} index - Index of the link to remove
     */
    const removeContentLink = (index) => {
      setFormData(prev => ({
        ...prev,
        content_links: prev.content_links.filter((_, i) => i !== index)
      }));
    };

    /**
     * Handle form submission for video updates
     * Includes video metadata, categories, and content links
     * All updates are sent to backend which handles replacement of categories/links atomically
     */
    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/videos/${video.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update video');
        }

        onSuccess();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setFormData(prev => ({
        ...prev,
        [e.target.name]: value
      }));
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Video</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vimeo URL *</label>
                <input
                  type="url"
                  name="vimeo_url"
                  value={formData.vimeo_url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Date</label>
                <input
                  type="date"
                  name="training_date"
                  value={formData.training_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Date when the training session was conducted</p>
              </div>
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                  <input
                    type="number"
                    name="duration_seconds"
                    value={formData.duration_seconds ? Math.floor(formData.duration_seconds / 60) : ''}
                    onChange={(e) => {
                      const minutes = parseInt(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        duration_seconds: minutes > 0 ? minutes * 60 : null
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter duration in minutes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                  <select
                    name="category_ids"
                    multiple
                    value={formData.category_ids || []}
                    onChange={(e) => {
                      const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({
                        ...prev,
                        category_ids: selectedIds
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {categories && categories.length > 0 ? (
                      categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))
                    ) : (
                      <option value="" disabled>No categories available</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple categories</p>
                </div>
              </div>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Featured Video</span>
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content Visibility *</label>
                <select
                  name="required_role"
                  value={formData.required_role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="member_paid">Founders (appears in main Videos section)</option>
                  <option value="advisor">Advisors Only (appears in Advisor Hub only)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Advisors can still access Founder content</p>
              </div>

              {/* Content Linking Section */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Content Links</h4>
                  <button
                    type="button"
                    onClick={addContentLink}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Link</span>
                  </button>
                </div>

                {formData.content_links.map((link, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-medium text-gray-900">Link {index + 1}</h5>
                      <button
                        type="button"
                        onClick={() => removeContentLink(index)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link Type *</label>
                        <select
                          value={link.link_type}
                          onChange={(e) => updateContentLink(index, 'link_type', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="prompt">AI Prompt</option>
                          <option value="resource">Resource</option>
                          <option value="external">External Link</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Link Text *</label>
                        <input
                          type="text"
                          value={link.link_text}
                          onChange={(e) => updateContentLink(index, 'link_text', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Use: Business Analysis Prompt"
                        />
                      </div>
                    </div>

                    {link.link_type === 'prompt' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Prompt *</label>
                        <select
                          value={link.content_id}
                          onChange={(e) => updateContentLink(index, 'content_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Choose a prompt...</option>
                          {prompts.map((prompt) => (
                            <option key={prompt.id} value={prompt.id}>
                              {prompt.is_multi_part ? `${prompt.title} (${prompt.total_parts} parts)` : prompt.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {link.link_type === 'resource' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Resource *</label>
                        <select
                          value={link.content_id}
                          onChange={(e) => updateContentLink(index, 'content_id', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Choose a resource...</option>
                          {resources.map((resource) => (
                            <option key={resource.id} value={resource.id}>
                              {resource.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {link.link_type === 'external' && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">External URL *</label>
                        <input
                          type="url"
                          value={link.external_url}
                          onChange={(e) => updateContentLink(index, 'external_url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://example.com"
                        />
                      </div>
                    )}

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={link.description}
                        onChange={(e) => updateContentLink(index, 'description', e.target.value)}
                        rows="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Optional description of how this relates to the video"
                      />
                    </div>
                  </div>
                ))}

                {formData.content_links && formData.content_links.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Link className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p>No content links added yet.</p>
                    <p className="text-sm">Click "Add Link" to connect prompts or resources to this video.</p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Updating...' : 'Update Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // Create User Modal Component
  const CreateUserModal = ({ onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      confirm_password: '',
      role: 'member_free',
      subscription_status: 'inactive',
      phone: '',
      company: '',
      position: '',
      send_invite: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Only validate password for non-advisor/staff/investor roles and when not sending invite
      if (formData.role !== 'advisor' && formData.role !== 'staff' && formData.role !== 'investor' && !formData.send_invite) {
        if (formData.password !== formData.confirm_password) {
          setError('Passwords do not match');
          return;
        }

        // Check password strength (same as registration)
        const hasUpper = /[A-Z]/.test(formData.password);
        const hasLower = /[a-z]/.test(formData.password);
        const hasNumber = /\d/.test(formData.password);
        const hasSpecial = /[@$!%*?&]/.test(formData.password);
        
        const isShortComplex = formData.password.length >= 8 && hasUpper && hasLower && hasNumber && hasSpecial;
        const isLongSimple = formData.password.length >= 16 && hasUpper && hasLower && hasNumber;
        
        if (!isShortComplex && !isLongSimple) {
          setError('Password must be either: 8+ chars with uppercase, lowercase, number, and special character, OR 16+ chars with uppercase, lowercase, and number');
          return;
        }
      }

      try {
        setLoading(true);
        setError('');
        
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/users`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email: formData.email,
            first_name: formData.first_name,
            last_name: formData.last_name,
            ...(formData.role !== 'advisor' && !formData.send_invite && { password: formData.password }),
            role: formData.role,
            subscription_status: formData.subscription_status,
            phone: formData.phone || null,
            company: formData.company || null,
            position: formData.position || null
          })
        });

        if (response.ok) {
          if (formData.send_invite) {
            alert(`User created successfully! You can now send an invite email from the user list.`);
          } else {
            alert(`User created successfully!`);
          }
          onSuccess();
        } else {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to create user');
        }
      } catch (error) {
        setError('Network error. Please try again.');
        console.error('Create user error:', error);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      
      setFormData(prev => {
        const newData = {
          ...prev,
          [name]: value
        };
        
        // Auto-set subscription status based on role
        if (name === 'role') {
          if (value === 'advisor' || value === 'admin') {
            newData.subscription_status = 'active'; // Free access
          } else if (value === 'member_free') {
            newData.subscription_status = 'active'; // Free access to free content
          } else if (value === 'member_paid') {
            newData.subscription_status = 'inactive'; // Needs subscription
          }
        }
        
        return newData;
      });
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New User</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    required
                    value={formData.first_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    required
                    value={formData.last_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Invite option - show first for non-advisor/staff/investor roles */}
              {formData.role !== 'advisor' && formData.role !== 'staff' && formData.role !== 'investor' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="send_invite"
                      checked={formData.send_invite}
                      onChange={handleChange}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div>
                      <span className="text-sm font-medium text-blue-900">Send invite email instead of setting password</span>
                      <p className="text-xs text-blue-700 mt-1">
                        User will receive an email to set their password. Account will be created inactive until they complete setup.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Password fields - only show for non-advisor/staff/investor roles when NOT sending invite */}
              {formData.role !== 'advisor' && formData.role !== 'staff' && formData.role !== 'investor' && !formData.send_invite && (
                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                    <input
                      type="password"
                      name="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password *</label>
                    <input
                      type="password"
                      name="confirm_password"
                      required
                      value={formData.confirm_password}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
              
              {/* Advisor/Staff/Investor role notice */}
              {(formData.role === 'advisor' || formData.role === 'staff' || formData.role === 'investor') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        <strong>{formData.role === 'staff' ? 'Staff Account:' : formData.role === 'investor' ? 'Investor Account:' : 'Advisor Account:'}</strong> Password will be set via email invite after account creation.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="member_free">Free Member</option>
                    <option value="member_paid">Paid Member</option>
                    <option value="advisor">Advisor</option>
                    <option value="staff">Staff</option>
                    <option value="investor">Investor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subscription Status</label>
                  <select
                    name="subscription_status"
                    value={formData.subscription_status}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="inactive">Inactive</option>
                    <option value="active">Active (Free/Paid)</option>
                    <option value="trialing">Trialing</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="past_due">Past Due</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.role === 'advisor' || formData.role === 'admin' 
                      ? 'Advisors and admins get free access (Active status)'
                      : formData.role === 'member_free'
                      ? 'Free members get access to free content (Active status)'
                      : 'Paid members need active subscription for full access'
                    }
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  /**
   * CreateResourceModal Component
   * 
   * Modal for creating new resources (links or guides) with conditional field display.
   * Shows different fields based on selected resource type:
   * - Link resources: Shows URL field
   * - Guide resources: Shows slug and markdown content fields
   * 
   * Features:
   * - Resource type dropdown (External Link or Guide)
   * - Conditional field rendering based on content_type
   * - Auto-slug generation for guides
   * - Category selection and role assignment
   * 
   * @param {Array} categories - Available categories for selection
   * @param {Function} onClose - Callback to close the modal
   * @param {Function} onSuccess - Callback when resource is successfully created
   */
  const CreateResourceModal = ({ categories, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      file_url: '',
      file_type: 'link',
      category_ids: [],
      required_role: 'member_paid', // Default to paid member - all resources accessible to paid members, advisors, and admins
      is_featured: false,
      content_type: 'link', // Default to link
      markdown_content: '',
      slug: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Debug: Log categories when modal opens
    // CreateResourceModal - Categories received

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/resources`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create resource');
        }

        const responseData = await response.json();
        onSuccess();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setFormData(prev => ({
        ...prev,
        [e.target.name]: value
      }));
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Resource</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Resource Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                <select
                  name="content_type"
                  value={formData.content_type}
                  onChange={(e) => {
                    const newContentType = e.target.value;
                    setFormData({
                      ...formData,
                      content_type: newContentType,
                      file_type: newContentType === 'guide' ? 'document' : 'link'
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="link">External Link</option>
                  <option value="guide">Guide (Markdown)</option>
                </select>
              </div>

              {/* Show URL field only for 'link' type */}
              {formData.content_type === 'link' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                  <input
                    type="url"
                    name="file_url"
                    value={formData.file_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                    required
                  />
                </div>
              )}

              {/* Show markdown editor for 'guide' type */}
              {formData.content_type === 'guide' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL-friendly)</label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="building-data-room"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave blank to auto-generate from title
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guide Content (Markdown) *</label>
                    <textarea
                      name="markdown_content"
                      value={formData.markdown_content}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      rows="20"
                      placeholder="# Guide Title&#10;&#10;Your markdown content here..."
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                <select
                  name="category_ids"
                  multiple
                  value={formData.category_ids || []}
                  onChange={(e) => {
                    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData(prev => ({
                      ...prev,
                      category_ids: selectedIds
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple categories</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content Visibility *</label>
                <select
                  name="required_role"
                  value={formData.required_role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="member_paid">Founders (appears in main Resources section)</option>
                  <option value="advisor">Advisors Only (appears in Advisor Hub only)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Advisors can still access Founder content</p>
              </div>
              
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Featured Resource</span>
                </label>
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Creating...' : 'Create Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  /**
   * EditResourceModal Component
   * 
   * Modal for editing existing resources (links or guides) with pre-populated data.
   * Shows conditional fields based on the resource's content_type:
   * - Link resources: Shows URL field with current value
   * - Guide resources: Shows slug and markdown content fields with current values
   * 
   * Features:
   * - Pre-populates form with existing resource data
   * - Resource type dropdown (External Link or Guide)
   * - Conditional field rendering based on content_type
   * - Category selection and role assignment
   * - Preserves existing markdown content for guides
   * 
   * @param {Object} resource - The resource object being edited
   * @param {Array} categories - Available categories for selection
   * @param {Function} onClose - Callback to close the modal
   * @param {Function} onSuccess - Callback when resource is successfully updated
   */
  const EditResourceModal = ({ resource, categories, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      title: resource?.title || '',
      description: resource?.description || '',
      file_url: resource?.file_url || '',
      file_type: resource?.file_type || 'document',
      category_ids: resource?.category_ids || [],
      required_role: resource?.required_role || 'member_paid',
      is_featured: resource?.is_featured || false,
      content_type: resource?.content_type || 'guide',
      markdown_content: resource?.markdown_content || '',
      slug: resource?.slug || ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/resources/${resource.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update resource');
        }

        onSuccess();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setFormData(prev => ({
        ...prev,
        [e.target.name]: value
      }));
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Resource</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Resource Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Resource Type</label>
                <select
                  name="content_type"
                  value={formData.content_type}
                  onChange={(e) => {
                    const newContentType = e.target.value;
                    setFormData({
                      ...formData,
                      content_type: newContentType,
                      file_type: newContentType === 'guide' ? 'document' : 'link'
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="link">External Link</option>
                  <option value="guide">Guide (Markdown)</option>
                </select>
              </div>

              {/* Show URL field only for 'link' type */}
              {formData.content_type === 'link' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                  <input
                    type="url"
                    name="file_url"
                    value={formData.file_url}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://example.com"
                    required
                  />
                </div>
              )}

              {/* Show markdown editor for 'guide' type */}
              {formData.content_type === 'guide' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL-friendly)</label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="building-data-room"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Leave blank to auto-generate from title
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Guide Content (Markdown) *</label>
                    <textarea
                      name="markdown_content"
                      value={formData.markdown_content}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      rows="20"
                      placeholder="# Guide Title&#10;&#10;Your markdown content here..."
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                <select
                  name="category_ids"
                  multiple
                  value={formData.category_ids || []}
                  onChange={(e) => {
                    const selectedIds = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData(prev => ({
                      ...prev,
                      category_ids: selectedIds
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple categories</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content Visibility *</label>
                <select
                  name="required_role"
                  value={formData.required_role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="member_paid">Founders (appears in main Resources section)</option>
                  <option value="advisor">Advisors Only (appears in Advisor Hub only)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Advisors can still access Founder content</p>
              </div>
              
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Featured Resource</span>
                </label>
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Updating...' : 'Update Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // Create Category Modal Component
  const CreateCategoryModal = ({ onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      color: '#3B82F6'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/categories`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create category');
        }

        onSuccess();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Create New Category</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

  // Edit Category Modal Component
  const EditCategoryModal = ({ category, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
      name: category?.name || '',
      description: category?.description || '',
      color: category?.color || '#3B82F6'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/categories/${category.id}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to update category');
        }

        onSuccess();
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const handleChange = (e) => {
      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    };

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Category</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded text-sm mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <input
                  type="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={onClose} className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {loading ? 'Updating...' : 'Update Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  };

// Prompts Management Component
const PromptsManagement = ({ 
  prompts, 
  categories, 
  onRefresh, 
  showCreateForm, 
  setShowCreateForm, 
  editingItem, 
  setEditingItem 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    prompt_text: '',
    usage_instructions: '',
    display_order: 0,
    is_active: true,
    moduleTitle: '',
    moduleDescription: '',
    totalParts: 1,
    parts: []
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = type === 'checkbox' ? checked : value;
    
    // Convert totalParts to number immediately
    if (name === 'totalParts') {
      processedValue = parseInt(value, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const handlePartChange = (partIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.map((part, index) => 
        index === partIndex ? { ...part, [field]: value } : part
      )
    }));
  };

  const addPart = () => {
    setFormData(prev => ({
      ...prev,
      parts: [...prev.parts, {
        title: '',
        description: '',
        prompt_text: '',
        usage_instructions: ''
      }],
      totalParts: prev.totalParts + 1
    }));
  };

  const removePart = (partIndex) => {
    setFormData(prev => ({
      ...prev,
      parts: prev.parts.filter((_, index) => index !== partIndex),
      totalParts: Math.max(1, prev.totalParts - 1)
    }));
  };

  const handleSubmit = async (e) => {
    // handleSubmit called
    e.preventDefault();
    try {
      const token = localStorage.getItem('authToken');
      const url = editingItem 
        ? `${import.meta.env.VITE_API_URL}/api/admin/prompts/${editingItem.id}`
        : `${import.meta.env.VITE_API_URL}/api/admin/prompts`;
      
      const method = editingItem ? 'PUT' : 'POST';
      
      // Convert totalParts to number
      const dataToSend = {
        ...formData,
        totalParts: parseInt(formData.totalParts, 10)
      };
      
      // Sending form data
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        onRefresh();
        setShowCreateForm(false);
        setEditingItem(null);
        setFormData({
          title: '',
          description: '',
          category_id: '',
          prompt_text: '',
          usage_instructions: '',
          display_order: 0,
          is_active: true,
          moduleTitle: '',
          moduleDescription: '',
          totalParts: 1,
          parts: []
        });
        alert(editingItem ? 'Prompt updated successfully!' : 'Prompt created successfully!');
      } else {
        const error = await response.json();
        alert('Error: ' + (error.error || 'Failed to save prompt'));
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Error saving prompt: ' + error.message);
    }
  };

  const handleEdit = (prompt) => {
    // Handle multi-part prompts differently
    if (prompt.is_multi_part && prompt.parts && prompt.parts.length > 0) {
      // For multi-part prompts, populate with the first part's data and module info
      const firstPart = prompt.parts[0];
      setFormData({
        title: firstPart.title,
        description: firstPart.description,
        category_id: prompt.category_id || '',
        prompt_text: firstPart.prompt_text,
        usage_instructions: firstPart.usage_instructions || '',
        display_order: prompt.display_order || 0,
        is_active: prompt.is_active,
        moduleTitle: prompt.title,
        moduleDescription: prompt.description,
        totalParts: prompt.total_parts,
        parts: prompt.parts.slice(1).map(part => ({
          title: part.title,
          description: part.description,
          prompt_text: part.prompt_text,
          usage_instructions: part.usage_instructions
        }))
      });
    } else {
      // For single prompts
      setFormData({
        title: prompt.title,
        description: prompt.description,
        category_id: prompt.category_id || '',
        prompt_text: prompt.prompt_text,
        usage_instructions: prompt.usage_instructions || '',
        display_order: prompt.display_order || 0,
        is_active: prompt.is_active,
        moduleTitle: '',
        moduleDescription: '',
        totalParts: 1,
        parts: []
      });
    }
    setEditingItem(prompt);
    setShowCreateForm(true);
  };

  const handleDelete = async (promptId) => {
    if (window.confirm('Are you sure you want to delete this prompt? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/admin/prompts/${promptId}`, {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          await onRefresh();
          alert('Prompt deleted successfully!');
        } else {
          alert('Failed to delete prompt');
        }
      } catch (error) {
        console.error('Error deleting prompt:', error);
        alert('Error deleting prompt: ' + error.message);
      }
    }
  };

  const getCategoryIcon = (categoryName) => {
    const iconMap = {
      'Marketing & Sales': '📊',
      'Financial Analysis': '💰',
      'Team Management': '👥',
      'Operations': '⚙️',
      'Product Development': '🚀',
      'Customer Research': '🔍'
    };
    return iconMap[categoryName] || '📝';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">AI Prompts Management</h2>
          <p className="text-gray-600">Manage AI prompts for the library</p>
        </div>
        <button
          onClick={() => {
            setShowCreateForm(true);
            setEditingItem(null);
            setFormData({
              title: '',
              description: '',
              category_id: '',
              prompt_text: '',
              usage_instructions: '',
              display_order: 0,
              is_active: true,
              moduleTitle: '',
              moduleDescription: '',
              totalParts: 1,
              parts: []
            });
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Prompt
        </button>
      </div>

      {/* Prompts List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {prompts.map((prompt) => (
                <tr key={prompt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{getCategoryIcon(prompt.category_name)}</span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{prompt.title}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">{prompt.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${prompt.category_color}20`, color: prompt.category_color }}
                    >
                      {prompt.category_name}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      prompt.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {prompt.is_active ? 'Published' : 'Unpublished'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(prompt.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(prompt)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(prompt.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingItem ? 'Edit Prompt' : 'Add New Prompt'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingItem(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Multi-Part Prompt Configuration */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium text-gray-900">Multi-Part Configuration</h4>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm font-medium text-gray-700">Number of Parts:</label>
                    <select
                      name="totalParts"
                      value={formData.totalParts}
                      onChange={handleChange}
                      className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={1}>1 Part (Single Prompt)</option>
                      <option value={2}>2 Parts</option>
                      <option value={3}>3 Parts</option>
                    </select>
                  </div>
                </div>

                {formData.totalParts > 1 && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Module Title *</label>
                        <input
                          type="text"
                          name="moduleTitle"
                          value={formData.moduleTitle}
                          onChange={handleChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="e.g., Business Analysis Suite"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Module Description *</label>
                        <textarea
                          name="moduleDescription"
                          value={formData.moduleDescription}
                          onChange={handleChange}
                          required
                          rows="2"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Overall description of the prompt module"
                        />
                      </div>
                    </div>

                    {/* Individual Parts */}
                    <div className="space-y-4">
                      <h5 className="font-medium text-gray-900">Individual Parts</h5>
                      
                      {/* Part 1 (main prompt) */}
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <h6 className="font-medium text-gray-900">Part 1 (Main Prompt)</h6>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Part Title *</label>
                            <input
                              type="text"
                              value={formData.title}
                              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="e.g., Market Analysis Prompt"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Part Description *</label>
                            <textarea
                              value={formData.description}
                              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                              required
                              rows="2"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="What this specific part does"
                            />
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Part Prompt Text *</label>
                          <textarea
                            value={formData.prompt_text}
                            onChange={(e) => setFormData(prev => ({ ...prev, prompt_text: e.target.value }))}
                            required
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                            placeholder="The actual prompt text for this part"
                          />
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Usage Instructions</label>
                          <textarea
                            value={formData.usage_instructions}
                            onChange={(e) => setFormData(prev => ({ ...prev, usage_instructions: e.target.value }))}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="How to use this specific part"
                          />
                        </div>
                      </div>

                      {/* Additional Parts */}
                      {formData.parts && formData.parts.map((part, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <h6 className="font-medium text-gray-900">Part {index + 2}</h6>
                            <button
                              type="button"
                              onClick={() => removePart(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Remove Part
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Part Title *</label>
                              <input
                                type="text"
                                value={part.title}
                                onChange={(e) => handlePartChange(index, 'title', e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g., Financial Analysis Prompt"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Part Description *</label>
                              <textarea
                                value={part.description}
                                onChange={(e) => handlePartChange(index, 'description', e.target.value)}
                                required
                                rows="2"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="What this specific part does"
                              />
                            </div>
                          </div>
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Part Prompt Text *</label>
                            <textarea
                              value={part.prompt_text}
                              onChange={(e) => handlePartChange(index, 'prompt_text', e.target.value)}
                              required
                              rows="4"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                              placeholder="The actual prompt text for this part"
                            />
                          </div>
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Usage Instructions</label>
                            <textarea
                              value={part.usage_instructions}
                              onChange={(e) => handlePartChange(index, 'usage_instructions', e.target.value)}
                              rows="2"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="How to use this specific part"
                            />
                          </div>
                        </div>
                      ))}

                      {/* Add Part Button */}
                      {formData.parts && formData.parts.length < formData.totalParts - 1 && (
                        <button
                          type="button"
                          onClick={addPart}
                          className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                        >
                          <Plus className="w-5 h-5 mx-auto mb-2" />
                          Add Part {formData.parts.length + 2}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Single prompt fields - only show when totalParts is 1 */}
              {formData.totalParts === 1 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prompt Text *</label>
                    <textarea
                      name="prompt_text"
                      value={formData.prompt_text}
                      onChange={handleChange}
                      required
                      rows="8"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder="Enter the full prompt text here..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Usage Instructions</label>
                    <textarea
                      name="usage_instructions"
                      value={formData.usage_instructions}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Step-by-step instructions for using this prompt..."
                    />
                  </div>
                </>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">Published</label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingItem(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingItem ? 'Update Prompt' : 'Create Prompt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};


export default AdminDashboard;
